import * as THREE from 'three';
import Stats from 'stats.js';
import {
  SplatMesh,
  SparkRenderer,
  PackedSplats,
  clearProgramMaterial,
  dyno,
} from '@sparkjsdev/spark';
import { SceneControls } from './controls/SceneControls';
import { SightManager } from './camera/SightManager';
import { CameraDirector, type CameraPose } from './camera/CameraDirector';
import type { CameraConfig } from '../cameraConfig';
import { AnalyticalCollisionSystem } from './collision/AnalyticalCollisionSystem';
import type { CollisionConfig } from './collision/colliderTypes';
import type { Sight, SightId } from '../types';
import { $appConfig, updateCameraConfig, updateColliders, updateSingleCollider, $previewModelConfig, updatePreviewModelConfig, $backgroundConfig, $tellerAssistantConfig, updateTellerAssistantConfig, $gizmoTargetId } from '../state/appConfig';
import { getCurtainOffset, HUD_REVEAL_DELAY } from '../context/CameraViewOffsetContext';
import { $safeAreaInsets } from '../state/safeArea';
import { $viewMode } from '../state/viewMode';
import { TellerAssistantShader } from './assistant/TellerAssistantShader';
import type { AppConfig } from '../state/appConfig';
import type { DevConfig, SparkRenderSettingsConfig } from '../devConfig';
import type { PoiConfig } from '../poiConfig';
import { resolveCdnUrl } from '../utils/cdnUrl';
import { loadWithCdnFallback } from '../utils/loadWithCdnFallback';
import { fetchWithTtfbTimeout } from '../utils/fetchWithTtfbTimeout';
import {
  readResponseBytesWithProgress,
  type ProgressCallback,
} from '../utils/readResponseBytes';
import type { TellerAssistantConfig } from '../tellerAssistantConfig';
import {
  $currentSight,
  $orbitAnimationEnabled,
  clearOrbitTargetOverride,
  userSelectedSight,
  setOrbitAnimationEnabled,
  userInteracted,
} from '../state/appState';
import { assistantPoiClickRequested } from '../state/assistantHud';
import { resetNavigation, enterScene } from '../state/sceneNavigation';
import { enterInsideScene } from '../state/insideSceneEntry';
import { isInsideScene } from './insideScenes/registry';
import { sceneEntered } from '../analytics/sceneTracker';
import { cameraPreloaderSkipped } from '../analytics/cameraSessionTracker';
import { insidePoiClicked } from '../analytics/navigationTracker';
import { $locale } from '../state/locale';
import { getMessages } from '../i18n';
import { KNOWN_SCENES, SCENE_POI_ID } from './sceneNavigationMap';
import {
  sceneLoadingFailed,
  sceneLoadingFinished,
  sceneLoadingProgressed,
  sceneLoadingStarted,
  scenePreviewLoadingFinished,
  splatLoadingProgressed,
  splatFetchCompleted,
  splatInitCompleted,
} from '../state/sceneLoading';
import {
  incrementScene,
  decrementScene,
  logEvent,
  logThreeJsDiagnostics,
  measureDisposeStep,
  logBigBuffer,
  trackPostDispose,
} from '../debug/memoryTracker';
import { acquireMainRenderer, releaseMainRenderer } from './mainRenderer';
import { detectQualityProfile, type QualityProfile, type DeviceTier } from './qualityProfile';
import { PrincipalPointController } from './camera/PrincipalPointController';
import { EnvironmentManager } from './environment/EnvironmentManager';
import { disposeSceneGraph } from './utils/disposeSceneResources';
import { PoiMarkerManager, type PoiMarkerDiagnostics } from './poi/PoiMarkerManager';
import { DevVisuals, ORBIT_CENTER_GIZMO_ID } from './dev/DevVisuals';
import { SceneBboxDebug } from './debug/sceneBboxDebug';
import { RearViewSparkModule } from './spark/RearViewSparkModule';
import { PreviewModel } from './appearance/PreviewModel';
import { PreviewTour } from './camera/PreviewTour';
import { CameraFlyover } from './camera/CameraFlyover';
import type { SceneConfig, SceneTransform, Vector3Like } from './sceneConfig';
import { $splatTransform, $splatDebugVisible, initSplatTransform } from '../state/splatTransform';
import {
  IDLE_RENDER_LOW_DELAY_MS,
  IDLE_RENDER_LOW_THRESHOLD_MS,
  IDLE_RENDER_MINIMAL_DELAY_MS,
  IDLE_RENDER_MINIMAL_THRESHOLD_MS,
  IDLE_RENDER_PAUSED_THRESHOLD_MS,
  type RenderTier,
} from './config/idleRender';
export type { PoiMarkerDiagnostics } from './poi/PoiMarkerManager';


const DEFAULT_OBJECT_SCALE = new THREE.Vector3(1, 1, 1);
const MAX_FRAME_DELTA_SECONDS = 0.1;
const ASSISTANT_RENDER_LAYER = 2;
const KEYBOARD_RENDER_WAKE_CODES = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'KeyR',
  'KeyF',
]);

const getDeviceRenderSettings = (
  config: DevConfig,
  profile: QualityProfile
): SparkRenderSettingsConfig => {
  const tiered = config.renderSettings[profile.deviceClass];
  return tiered[profile.tier];
};

async function fetchBinaryWithProgress(
  url: string,
  onProgress?: ProgressCallback
): Promise<Uint8Array> {
  const response = await fetchWithTtfbTimeout(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
  }

  return readResponseBytesWithProgress(response, onProgress);
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable) {
    return true;
  }
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  );
}

/**
 * Snapshot of the camera framing (position + target + fov). Used by the
 * scene-state persistence path (`src/state/sceneState.ts`) when the user
 * leaves a main scene for an inside-scene — on return we restore this
 * pose via `initialCameraPose` so the camera lands exactly where it left
 * off, no flyover, no preview tour.
 *
 * Plain numeric shape so non-Three.js callers (Effector stores) can
 * persist it without dragging in `THREE.Vector3` instances.
 */
export type SplatCameraPose = {
  position: Vector3Like;
  target: Vector3Like;
  fov: number;
};

/**
 * DEV-only API for live-tuning nav-POI positions and toggling the red debug
 * dot. Exposed via `SceneBindings.navPoiTuner` when the active scene has
 * `navigationPois` defined. Consumed by the `NavPoiTuner` Leva component
 * (`?dev` mode). `null` on scenes without nav-POIs and after dispose.
 */
export interface NavPoiTunerApi {
  /** Scene ids of nav-POIs in this scene, e.g. ['mina','muzdalifah','arafat','kaaba']. */
  readonly navPoiSceneIds: readonly string[];
  /** Current 3D position of a nav-POI; `null` if `sceneId` is not a nav-POI. */
  getNavPoiPosition: (sceneId: string) => { x: number; y: number; z: number } | null;
  /** Partial update — only axes present in `patch` are written. Triggers re-render. */
  setNavPoiPosition: (sceneId: string, patch: { x?: number; y?: number; z?: number }) => void;
  /** Toggle the red debug dot at each beacon's 3D anchor projection. */
  setDebugDotVisible: (visible: boolean) => void;
}

export interface SceneBindings {
  camera: THREE.PerspectiveCamera;
  controls: SceneControls;
  sightManager: SightManager;
  renderer: THREE.WebGLRenderer;
  sparkRenderer: SparkRenderer;
  scene: THREE.Scene;
  getPoiMarkerDiagnostics: () => PoiMarkerDiagnostics[];
  tellerAssistant: TellerAssistantShader | null;
  /**
   * Returns the camera's current pose (position, target, fov). Used by
   * `saveCurrentSceneSnapshot` to capture the framing before the user
   * navigates away. Nullified to a zero-pose getter on dispose — callers
   * tolerate the empty result and skip the save.
   */
  getCurrentCameraPose: () => SplatCameraPose;
  /**
   * Force-completes the preloader cinematic (preview-tour + flyover),
   * hides the input blocker, and restores orbit locks. Used by
   * `RestrictedCameraTuner.Unlock` so the dev can tune the camera before
   * the scene has fully finished loading. Idempotent — safe to call at
   * any point in the scene lifecycle.
   */
  forceFinishPreloader: () => void;
  /** DEV-only nav-POI live tuner. `null` if scene has no navigationPois. */
  navPoiTuner: NavPoiTunerApi | null;
}

/**
 * Camera pose for overview transitions (e.g. `OverallSceneOverlay` swipe-
 * driven camera focus). Plain Vector3Like — caller doesn't need to import
 * THREE just to call `splatApi.transitionToPose`.
 */
export type OverviewCameraPose = {
  position: Vector3Like;
  target: Vector3Like;
};

export interface SplatSceneApi {
  dispose: () => void;
  pause: () => void;
  resume: () => void;
  /**
   * Reads the current camera framing. Used by `saveCurrentSceneSnapshot`
   * just before the user leaves a main scene for an inside-scene — the
   * pose is later replayed via `initialCameraPose` on return so the
   * camera lands exactly where it was. NOOP-equivalent after dispose
   * (returns a zero-pose).
   */
  getCurrentCameraPose: () => SplatCameraPose;
  /**
   * Cinematic camera transition to a given pose. Auto-cancels any in-flight
   * transition and starts a fresh one from the current camera pose. Default
   * duration ~1.5s with `ease-in-out`. Vertical-constraint / height-clamp
   * are disabled (overall scene's free-orbit doesn't need them, and they
   * would otherwise fight the swipe-driven target). NOOP after dispose.
   */
  transitionToPose: (pose: OverviewCameraPose, durationSec?: number) => void;
  /**
   * Triggers the same cinematic-fly-in + scene-change flow as a 3D
   * nav-POI marker click. Used by `OverallSceneOverlay` for the "Explore
   * {scene}" button on scene-banner cards so card-driven navigation
   * matches the 3D-marker UX (cinematic ~2.5s → scene swap at 70%).
   *
   * - `targetSceneId` must match a `NavigationPoi.targetSceneId` (= a
   *   URL id in `KNOWN_SCENES`). Unknown ids → console.warn + NOOP.
   * - Re-entrant guard: if another nav-cinematic is already pending,
   *   the call is dropped silently.
   * - NOOP after dispose.
   *
   * Inside-scene navigation (`kaaba-inside`, …) is NOT supported here —
   * those use direct `enterScene` (no cinematic infrastructure on the
   * splat scene level).
   */
  navigateToScene: (targetSceneId: string) => void;
}

interface SceneOptions {
  container: HTMLElement;
  sights: Sight[];
  config: SceneConfig;
  qualityProfile?: QualityProfile;
  onReady?: (bindings: SceneBindings) => void;
  skipAnimations?: boolean;
  /**
   * If provided, overrides the calculated `previewTourEnd` / flyover-tail
   * pose in the `skipAnimations` branch. Used by `SceneCanvas` when
   * mounting a scene that has a saved `SceneStateSnapshot`: we want the
   * camera to land exactly where the user left it before entering an
   * inside-scene, not at the scene's default initial pose.
   *
   * Ignored when `skipAnimations === false` (preview-tour / flyover own
   * the pose timeline in that case).
   */
  initialCameraPose?: SplatCameraPose;
}

// CRITICAL: these helpers MUST be declared at MODULE level, NOT inside
// `createScene`. Heap snapshot proved (path: SightManager ← Context.sightManager
// ← closure 'NOOP' ← api.dispose ← Context.splatApi ← FiberNode) that any
// function declared inside createScene captures the entire createScene IIFE
// scope via `[[Scopes]]` — even if the function body uses NOTHING from that
// scope. So `api.dispose = NOOP` (where NOOP is a createScene-local arrow)
// did the OPPOSITE of breaking the closure chain: it KEPT pinning the entire
// scope through NOOP's `[[Scopes]]`. Top-level declarations have no enclosing
// function scope, so they are GC-neutral.
const NOOP = (): void => {};
const EMPTY_POI_DIAGNOSTICS: PoiMarkerDiagnostics[] = [];
const EMPTY_POI_DIAGNOSTICS_GETTER = (): PoiMarkerDiagnostics[] => EMPTY_POI_DIAGNOSTICS;
const EMPTY_CAMERA_POSE: SplatCameraPose = {
  position: { x: 0, y: 0, z: 0 },
  target: { x: 0, y: 0, z: 0 },
  fov: 0,
};
const EMPTY_CAMERA_POSE_GETTER = (): SplatCameraPose => EMPTY_CAMERA_POSE;
// Module-level neutral getter for nav-POI position after dispose. Declared
// outside `createScene` so it does NOT capture the IIFE scope chain (would
// otherwise pin scene/camera/poiManager via [[Scopes]]). See CLAUDE.md § 9
// rule #10.
const EMPTY_NAV_POI_POSITION_GETTER = (): { x: number; y: number; z: number } | null => null;

export function createScene({
  container,
  sights,
  config,
  qualityProfile: qualityProfileParam,
  onReady,
  skipAnimations,
  initialCameraPose,
}: SceneOptions): SplatSceneApi {
  const qualityProfile = qualityProfileParam ?? detectQualityProfile();

  // Synthetic sights for top-level navigation POIs (used on the root
  // `overall` scene to navigate to child scenes). Click handler in
  // `onNavigateToSight` callback (below) branches on `id.startsWith('nav:')`.
  //
  // Locale snapshot at scene mount — markers do NOT live-update on locale
  // change. Acceptable for `overall`: locale switch happens via MenuOverlay
  // which triggers a React remount → scene rebuild → fresh labels. If hot-
  // swap-without-remount becomes a requirement, add a `$locale` subscription
  // here + `PoiMarkerManager.updateLabels` API + unsubscribe in Etap-8
  // nullification (see CLAUDE.md rule #14).
  const navigationPois = config.navigationPois ?? [];
  // Guard for nav-POI cinematic: blocks duplicate clicks while a fly-in
  // animation is mid-flight (the user shouldn't be able to retarget the
  // cinematic to a different POI; it's a one-shot per scene exit).
  let navTransitionPending = false;
  // Forward-declared stub (reassigned after `cameraDirector` / `controls`
  // are constructed, see end of createScene). Same pattern as
  // `forceFinishPreloader`. PoiMarkerManager's `onNavigateToSight`
  // callback captures it by closure — invoked only after the splat scene
  // has finished init, so the real implementation is always installed by
  // the time a click happens. Also exposed externally via
  // `SplatSceneApi.navigateToScene`.
  let runNavigationCinematic: (targetSceneId: string) => void = NOOP;
  const navMessages = getMessages($locale.getState()).globalMap.markerLabels;
  const navigationSights: Sight[] = navigationPois.map((poi) => ({
    id: `nav:${poi.targetSceneId}` as SightId,
    sceneId: config.id,
    name: navMessages[poi.labelKey] ?? poi.targetSceneId,
    position: poi.position,
    moveToSceneId: poi.targetSceneId,
  }));
  const allSights: Sight[] = navigationSights.length > 0
    ? [...sights, ...navigationSights]
    : sights;

  logEvent('createScene start (splat)', { sceneId: config.id });
  // DIAGNOSTIC: baseline BEFORE any allocation — tells us what persisted from
  // previous scene (domCanvases, domImages, shaderPrograms accumulation).
  logThreeJsDiagnostics('[splat] pre-renderer', { sceneId: config.id });
  incrementScene('splat');

  // Shared main renderer — iOS OOM fix. Splat scene does NOT own the
  // WebGLRenderer lifecycle; it acquires the app-wide singleton and releases
  // on dispose. See `mainRenderer.ts` for the contract.
  const mainRendererClaimantId = `splat-${config.id}`;
  container.innerHTML = '';
  const renderer = acquireMainRenderer(mainRendererClaimantId, container);
  renderer.setClearColor(config.renderer.clearColor, 1);
  // Splat requires touch-action: none (custom gesture handling).
  renderer.domElement.style.touchAction = 'none';
  logThreeJsDiagnostics('[splat] post-renderer-acquired', {
    renderer,
    sceneId: config.id,
  });

  const staticFrameOverlay = document.createElement('canvas');
  const staticFrameOverlayContext = staticFrameOverlay.getContext('2d', {
    alpha: true,
    desynchronized: true,
  });
  staticFrameOverlay.style.position = 'absolute';
  staticFrameOverlay.style.inset = '0';
  staticFrameOverlay.style.width = '100%';
  staticFrameOverlay.style.height = '100%';
  staticFrameOverlay.style.pointerEvents = 'none';
  staticFrameOverlay.style.display = 'none';

  // Transparent input blocker used during the preview-tour camera animation:
  // covers the canvas, swallows pointer/touch/wheel events so OrbitControls
  // never sees them — but OrbitControls.update() still runs internally so the
  // auto-rotate animation plays normally. Cheaper than messing with
  // controls.enabled (which early-returns from update() and freezes auto-rotate).
  const previewInputBlocker = document.createElement('div');
  previewInputBlocker.style.cssText =
    'position:absolute;inset:0;z-index:5;pointer-events:auto;touch-action:none;background:transparent;';
  previewInputBlocker.style.display = 'none';
  let clock = new THREE.Clock();
  const urlParams = new URL(window.location.href).searchParams;
  const isMobilePreview = urlParams.get('viewMode') === 'mobile';
  // In mobile preview, position:fixed elements are contained by the .screen ancestor
  // (which has transform). Use its constant dimensions instead of the dynamic .canvasRoot.
  const mobilePreviewScreen = isMobilePreview
    ? (() => {
        let el = container.parentElement;
        while (el) {
          if (getComputedStyle(el).transform !== 'none') return el;
          el = el.parentElement;
        }
        return null;
      })()
    : null;
  const getViewportWidth = () => mobilePreviewScreen ? mobilePreviewScreen.clientWidth : window.innerWidth;
  const getViewportHeight = () => mobilePreviewScreen ? mobilePreviewScreen.clientHeight : window.innerHeight;
  let canvasWidth = getViewportWidth();
  let canvasHeight = getViewportHeight();
  const isDevMode = import.meta.env.DEV || urlParams.has('dev');
  // Initialize colliders store with scene defaults
  const sceneDefaultColliders = config.colliders ?? [];
  updateColliders(structuredClone(sceneDefaultColliders));

  const initialAppConfig = $appConfig.getState();
  const isMobile = qualityProfile.deviceClass === 'mobile';
  // Resolve `*Mobile` look-sensitivity overrides into base fields, stripping
  // the *Mobile keys so they don't leak into the final CameraConfig spread.
  // See `CameraControlsOverrides.lookSpeedHorizontal` doc for why mobile needs
  // its own values (viewport width normalization).
  const resolvedControlsOverrides = (() => {
    const o = config.controls;
    if (!o) return null;
    const {
      lookSpeedHorizontalMobile,
      lookSpeedVerticalMobile,
      dragInertiaMobile,
      ...base
    } = o;
    if (!isMobile) return base;
    const resolved = { ...base };
    if (lookSpeedHorizontalMobile !== undefined) resolved.lookSpeedHorizontal = lookSpeedHorizontalMobile;
    if (lookSpeedVerticalMobile !== undefined) resolved.lookSpeedVertical = lookSpeedVerticalMobile;
    if (dragInertiaMobile !== undefined) resolved.dragInertia = dragInertiaMobile;
    return resolved;
  })();
  let latestCameraConfig: CameraConfig = resolvedControlsOverrides
    ? { ...initialAppConfig.camera, ...resolvedControlsOverrides }
    : initialAppConfig.camera;
  let currentDevConfig: DevConfig = initialAppConfig.dev;
  // Forward-declared so the `applyDevConfig` watcher can call the closure
  // before it's defined. Reassigned to the real implementation right after
  // `bindings` is built (~line 1580). Mid-session false→true toggles of
  // `dev.skipPreloaderAnimation` go through here.
  let forceFinishPreloader: () => void = NOOP;
  let previewHiddenAfterReveal = false;
  let latestTellerAssistantConfig: TellerAssistantConfig = initialAppConfig.tellerAssistant;
  let hasAppliedInitialCameraViewOffset = false;
  let activeSparkRenderSettings = getDeviceRenderSettings(initialAppConfig.dev, qualityProfile);
  let needsSparkUpdate = true;
  let renderSchedulerReady = false;
  let isPaused = false;

  // Etap 8 (KILLER fix for IIFE-scope retention via WebKit listener leaks):
  // All heavy bindings declared as `let` so they can be nulled out at end of
  // dispose. Background: heap snapshot proved that `onResize` (passed to
  // `new ResizeObserver(onResize)`) remains in `V8ResizeObserverCallback`
  // even AFTER `disconnect()` — same for window addEventListener handlers.
  // These leaked closures pin the entire createScene IIFE Context (488 bytes,
  // 121 slots) which contains every heavy ref (scene/camera/sparkRenderer/...).
  // We can't unbind the leaked listener from V8 internals, but we CAN null
  // out the IIFE Context slots themselves, so the leaked closure holds an
  // empty scope. The slots are accessed only via `let X = ...`, never via
  // `const`, so we can reassign at end of dispose.
  // Type stays non-nullable: usage sites don't need `?.` / `!`. Runtime
  // nullification uses `as unknown as T` cast (JS truly assigns null).
  let scene = new THREE.Scene();

  let devVisuals = new DevVisuals(scene, isDevMode);
  // Bbox debug visualizer: created lazily on first toggle of `debugBboxes`
  // dev flag, disposed when toggled off. Decoupled from scene lifetime.
  let sceneBboxDebug = null as SceneBboxDebug | null;
  // Mutable refs for previewModel / splatMesh so applyDevConfig (called
  // synchronously inside applyAppConfig BEFORE `previewModel` / `splatMesh`
  // `let`s are initialized — TDZ) can pass them to SceneBboxDebug without
  // touching the let-bindings directly.
  const bboxDebugRefs: { previewModel: PreviewModel | null; splatMesh: SplatMesh | null } = {
    previewModel: null,
    splatMesh: null,
  };
  let collisionSystem = new AnalyticalCollisionSystem(
    config.colliders ?? [],
    initialAppConfig.collision,
  );
  // Resolve splat selection EARLY so the environment manager is initialized
  // with the variant's skybox URL on its first preload — avoiding a redundant
  // second preload of a 30–128 MB EXR.
  //
  // Why this matters (iOS OOM-relevant): EnvironmentManager.preloadSkyboxTexture
  // begins fetching + decoding the EXR in its constructor (when given an
  // initial URL). The previous code passed the BASE config skybox URL and then,
  // ~580 lines later, called `environmentManager.setSkyboxUrl(variantUrl)` if
  // a variant overrode it — triggering a SECOND in-flight EXR load. The first
  // load's onLoad callback respects `preloadAbortId` and disposes its texture
  // on arrival, but until that point both fetches consume CPU memory in
  // parallel (Float32Array for HDR EXR ≈ 30–128 MB EACH at 4K). On a
  // memory-pressured device, the overlap window can tip the tab over the OOM
  // threshold during scene transitions.
  const splatParam = urlParams.get('splat');
  const splatSelection = resolveSplatSelection(splatParam, config.splat, qualityProfile);
  const splatURL = splatSelection.url;
  const splatVariant = splatSelection.variant;

  const baseSkyboxConfig = config.environment.background.skybox;
  const variantSkyboxConfig = splatVariant?.skybox;
  const effectiveSkyboxUrl = variantSkyboxConfig
    ? (isMobile ? (variantSkyboxConfig.mobileUrl ?? variantSkyboxConfig.url) : variantSkyboxConfig.url)
    : baseSkyboxConfig
    ? (isMobile ? (baseSkyboxConfig.mobileUrl ?? baseSkyboxConfig.url) : baseSkyboxConfig.url)
    : undefined;
  const effectiveSkyboxIntensity = variantSkyboxConfig
    ? (isMobile ? (variantSkyboxConfig.mobileIntensity ?? variantSkyboxConfig.intensity) : variantSkyboxConfig.intensity)
    : null;

  let environmentManager = new EnvironmentManager({
    scene,
    isDevMode,
    config: config.environment,
    initialSkyboxUrl: effectiveSkyboxUrl,
    onVisualChange: () => {
      if (renderSchedulerReady) {
        requestRender();
      }
    },
  });
  // Variant intensity override (cheap — does not trigger a preload).
  if (effectiveSkyboxIntensity != null) {
    environmentManager.setSkyboxIntensity(effectiveSkyboxIntensity);
  }
  environmentManager.addLighting();

  let poiManager = new PoiMarkerManager({
    container,
    gestureTarget: renderer.domElement,
    collisionSystem,
    onNavigateToSight: (sightId: SightId) => {
      // Navigation POIs (`overall` scene → child scenes) are encoded as
      // synthetic sights with `nav:<sceneId>` id prefix. They MUST NOT
      // dispatch userSelectedSight (would call sightManager.transitionToSight
      // on a locked-camera scene + emit a stray tour event) or
      // assistantPoiClickRequested (would POST to backend despite disableHud).
      const idStr = sightId.toString();
      if (idStr.startsWith('nav:')) {
        runNavigationCinematic(idStr.slice(4));
        return;
      }
      // Inside-scene POIs (sights with `moveToSceneId` → inside-scene id):
      // direct push onto the stack via the shared helper. Same path as
      // chat scene-banner clicks — no backend POST, no chat side-effects.
      const sight = allSights.find((s) => s.id === sightId);
      if (sight?.moveToSceneId && isInsideScene(sight.moveToSceneId)) {
        insidePoiClicked({
          sightId: idStr,
          sightName: sight.name ?? null,
          targetSceneId: sight.moveToSceneId,
        });
        enterInsideScene(sight.moveToSceneId);
        return;
      }
      userSelectedSight({ id: sightId, smooth: true, source: 'poi' });
      assistantPoiClickRequested({ id: sightId });
    },
    onClusterZoom: (cluster) => {
      const target = new THREE.Vector3(cluster.x, cluster.y, cluster.z);
      const currentDist = camera.position.distanceTo(target);
      const dir = new THREE.Vector3().subVectors(camera.position, target).normalize();
      const newPos = target.clone().addScaledVector(dir, currentDist * 0.5);
      cameraDirector.transitionToPose({ position: newPos, target }, 1.5);
      requestRender();
    },
    initialConfig: config.poi
      ? { ...initialAppConfig.poi, ...config.poi }
      : initialAppConfig.poi,
  });

  // Nav-POI dev tuner (active only when scene has navigationPois). Exposed
  // both as `SceneBindings.navPoiTuner` (consumed by NavPoiTuner Leva
  // panel) and a console shortcut `window.__tuneNavPoi(sceneId, y|patch)`.
  // Live-mutates `sight.position` + `marker.position` (in-place) so
  // navigation cinematic / pose math uses the updated values from the next
  // tick onward. Final tuned values must be copied back to overall.ts
  // (positions reset on page reload).
  let navPoiTuner: NavPoiTunerApi | null = null;
  if (navigationPois.length > 0) {
    const findNavSight = (sceneId: string) =>
      navigationSights.find((s) => s.id === `nav:${sceneId}` as SightId) ?? null;
    navPoiTuner = {
      navPoiSceneIds: navigationPois.map((p) => p.targetSceneId),
      getNavPoiPosition: (sceneId) => {
        const sight = findNavSight(sceneId);
        if (!sight?.position) return null;
        return { x: sight.position.x, y: sight.position.y, z: sight.position.z };
      },
      setNavPoiPosition: (sceneId, patch) => {
        const sight = findNavSight(sceneId);
        if (!sight?.position) {
          console.warn(`[NavPoiTuner] nav-POI not found: ${sceneId}`);
          return;
        }
        if (patch.x !== undefined) sight.position.x = patch.x;
        if (patch.y !== undefined) sight.position.y = patch.y;
        if (patch.z !== undefined) sight.position.z = patch.z;
        poiManager.setPosition(sight.id, sight.position.x, sight.position.y, sight.position.z);
        requestRender();
      },
      setDebugDotVisible: (visible) => {
        poiManager.setDebugDotVisible(visible);
        requestRender();
      },
    };

    if (isDevMode) {
      // Console shortcut: `__tuneNavPoi('kaaba', 0.30)` or `__tuneNavPoi('kaaba', { y: 0.30, x: 2.4 })`
      const shortcut = (sceneId: string, patch: number | { x?: number; y?: number; z?: number }) => {
        const obj = typeof patch === 'number' ? { y: patch } : patch;
        navPoiTuner?.setNavPoiPosition(sceneId, obj);
        const pos = navPoiTuner?.getNavPoiPosition(sceneId);
        if (pos) console.log(`[__tuneNavPoi] ${sceneId} → x=${pos.x.toFixed(3)} y=${pos.y.toFixed(3)} z=${pos.z.toFixed(3)}`);
      };
      (window as { __tuneNavPoi?: typeof shortcut }).__tuneNavPoi = shortcut;
      console.log(
        `[__tuneNavPoi] available: ${navigationPois.map((p) => `'${p.targetSceneId}'`).join(', ')}`,
      );
    }
  }

  const splatEncoding = { ...initialAppConfig.dev.splatEncoding };

  let sparkRenderer = new SparkRenderer({
    renderer,
    clock,
    splatEncoding,
    blurAmount: activeSparkRenderSettings.blurAmount,
    preBlurAmount: activeSparkRenderSettings.preBlurAmount,
    maxStdDev: activeSparkRenderSettings.maxStdDev,
    minPixelRadius: activeSparkRenderSettings.minPixelRadius,
    maxPixelRadius: activeSparkRenderSettings.maxPixelRadius,
    autoUpdate: activeSparkRenderSettings.autoUpdate,
    premultipliedAlpha: activeSparkRenderSettings.premultipliedAlpha,
    preUpdate: activeSparkRenderSettings.preUpdate,
    originDistance: activeSparkRenderSettings.originDistance,
    minAlpha: activeSparkRenderSettings.minAlpha,
    enable2DGS: activeSparkRenderSettings.enable2DGS,
    focalDistance: activeSparkRenderSettings.focalDistance,
    apertureAngle: activeSparkRenderSettings.apertureAngle,
    falloff: activeSparkRenderSettings.falloff,
    clipXY: activeSparkRenderSettings.clipXY,
    focalAdjustment: activeSparkRenderSettings.focalAdjustment,
    view: {
      autoUpdate: false,
      sortRadial: activeSparkRenderSettings.sortRadial,
      sort32: activeSparkRenderSettings.sort32,
      sortDistance: activeSparkRenderSettings.sortDistance,
      sortCoorient: activeSparkRenderSettings.sortCoorient,
      depthBias: activeSparkRenderSettings.depthBias,
      sort360: activeSparkRenderSettings.sort360,
      stochastic: activeSparkRenderSettings.stochastic,
    },
  });
  scene.add(sparkRenderer);
  sparkRenderer.renderOrder = -1;

  let camera = new THREE.PerspectiveCamera(
    config.camera.fov,
    canvasWidth / Math.max(1, canvasHeight),
    config.camera.near,
    config.camera.far
  );
  camera.position.set(
    config.camera.initialPosition.x,
    config.camera.initialPosition.y,
    config.camera.initialPosition.z
  );
  camera.layers.set(0);
  camera.layers.enable(ASSISTANT_RENDER_LAYER);
  scene.add(camera);
  let rearViewModule = new RearViewSparkModule({
    scene,
    camera,
    sparkRenderer,
  });

  let principalPointController = new PrincipalPointController(camera, clock);
  
  // Teller assistant is created lazily — only when showTellerAssistant is enabled.
  // This avoids allocating GPU resources (geometry, materials, textures) when the
  // assistant is not needed.
  let tellerAssistant: TellerAssistantShader | null = null;
  let createTellerAssistant: (() => Promise<void>) | null = null;

  const setRendererSize = () => {
    const configuredPixelRatioCap = activeSparkRenderSettings.pixelRatio;
    const pixelRatioCap = Number.isFinite(configuredPixelRatioCap)
      ? Math.max(0.25, configuredPixelRatioCap)
      : 1;
    const pixelRatio = Math.min(window.devicePixelRatio ?? 1, pixelRatioCap);
    renderer.setPixelRatio(pixelRatio);
    const vw = getViewportWidth();
    const vh = getViewportHeight();
    renderer.setSize(vw, vh);
    const canvas = renderer.domElement;
    canvasWidth = canvas.clientWidth || vw;
    canvasHeight = canvas.clientHeight || vh;
    camera.aspect = canvasWidth / Math.max(1, canvasHeight);
    camera.updateProjectionMatrix();
    principalPointController.setCanvasSize(canvasWidth, canvasHeight);

    if (
      staticFrameOverlay.width !== canvas.width ||
      staticFrameOverlay.height !== canvas.height
    ) {
      staticFrameOverlay.width = canvas.width;
      staticFrameOverlay.height = canvas.height;
    }
  };

  const minimizeDrawingBuffer = (): void => {
    renderer.setPixelRatio(1);
    renderer.setSize(1, 1, false);
    staticFrameOverlay.width = 1;
    staticFrameOverlay.height = 1;
  };

  const restoreDrawingBuffer = (): void => {
    setRendererSize();
    sparkRenderer.needsUpdate = true;
    needsSparkUpdate = true;
  };

  setRendererSize();
  container.appendChild(staticFrameOverlay);
  container.appendChild(previewInputBlocker);
  // renderer.domElement already attached to container + styled (absolute, inset 0,
  // touchAction none) via acquireMainRenderer + post-acquire setup above.
  // Camera controls — `SceneControls` (orbit + first-person). Restricted
  // (top-down) cameras for scenes like `overall` are achieved through
  // `camera.controls` overrides on top of the standard SceneControls
  // (`panMode: 'groundPlane'`, locked azimuth/polar, panBoundsXZ).
  let controls: SceneControls = new SceneControls(camera, renderer.domElement);
  // Collision system is intentionally NOT wired up here — preview-tour camera
  // should fly freely (no clamping to colliders). It's enabled after the tour
  // completes (search for the matching `controls.collisionSystem = collisionSystem`
  // assignments in the preview-tour terminators below).
  let isOrbitCenterGizmoDragging = false;
  if (isDevMode) {
    devVisuals.initGizmo(
      camera,
      renderer.domElement,
      (id, position) => {
        if (id === ORBIT_CENTER_GIZMO_ID) {
          updateCameraConfig({
            orbitCenter: { x: position.x, y: position.y, z: position.z },
          });
        } else {
          updateSingleCollider({ id, patch: { position } });
        }
      },
      (dragging) => {
        controls.enabled = !dragging;
        isOrbitCenterGizmoDragging =
          dragging && devVisuals.currentGizmoTargetId === ORBIT_CENTER_GIZMO_ID;
      },
      () => requestRender(),
    );
  }

  let cameraDirector = new CameraDirector(camera, controls, initialAppConfig.camera);

  /**
   * Real implementation of the nav-POI cinematic fly-in (= the same flow
   * a 3D nav-POI marker click triggers). Shared by:
   *   - `PoiMarkerManager.onNavigateToSight` (`nav:<sceneId>`-prefixed
   *     synthetic sight ids — see line 446).
   *   - `SplatSceneApi.navigateToScene` (`OverallSceneOverlay` card
   *     "Explore {scene}" buttons).
   *
   * Single-flight: re-entrant clicks are dropped silently while one
   * cinematic is in motion (`navTransitionPending` guard).
   *
   * On unknown / missing POI — bypasses the cinematic and runs the
   * scene-change immediately (still consistent UX: "click → enter scene").
   *
   * Detailed timing rationale (early-trigger at 70%, ease-in-out, etc.)
   * is in CLAUDE.md → "Navigation POIs (top-of-stack scenes)" §
   * "Cinematic fly-in before scene change".
   */
  runNavigationCinematic = (targetSceneId: string): void => {
    if (!KNOWN_SCENES.has(targetSceneId)) {
      console.warn('[createScene] navigation target unknown:', targetSceneId);
      return;
    }
    if (navTransitionPending) return;
    navTransitionPending = true;

    const triggerSceneChange = () => {
      resetNavigation();
      enterScene({
        sceneId: targetSceneId,
        poiFilterId: SCENE_POI_ID[targetSceneId] ?? targetSceneId,
      });
      sceneEntered(targetSceneId);
    };

    const targetPoi = navigationPois.find((p) => p.targetSceneId === targetSceneId);
    if (!targetPoi) {
      triggerSceneChange();
      return;
    }

    const orbit = controls.orbit;
    const newTarget = new THREE.Vector3(
      targetPoi.position.x,
      targetPoi.position.y,
      targetPoi.position.z,
    );
    const dir = new THREE.Vector3()
      .subVectors(camera.position, orbit.target)
      .normalize();
    const targetDistance = orbit.minDistance;
    const newPosition = newTarget.clone().addScaledVector(dir, targetDistance);

    previewInputBlocker.style.display = 'block';
    controls.suspendOrbitLocks();

    const durationCfg = config.navigationCinematicDuration;
    let transitionDurationSec: number;
    if (typeof durationCfg === 'number') {
      transitionDurationSec = durationCfg;
      console.log(
        `[nav-POI] → ${targetSceneId} · fixed duration=${transitionDurationSec}s`,
      );
    } else if (durationCfg) {
      const flightDistance = camera.position.distanceTo(newPosition);
      const isFar = flightDistance > durationCfg.thresholdDistance;
      transitionDurationSec = isFar ? durationCfg.far : durationCfg.close;
      console.log(
        `[nav-POI] → ${targetSceneId} · flightDist=${flightDistance.toFixed(2)}u`
          + ` threshold=${durationCfg.thresholdDistance}u`
          + ` → ${isFar ? 'FAR' : 'CLOSE'} · duration=${transitionDurationSec}s`,
      );
    } else {
      transitionDurationSec = 2.5;
    }
    const earlyTriggerMs = Math.round(transitionDurationSec * 1000 * 0.7);
    let triggered = false;
    const triggerOnce = () => {
      if (triggered) return;
      triggered = true;
      triggerSceneChange();
    };
    window.setTimeout(triggerOnce, earlyTriggerMs);

    cameraDirector.transitionToPose(
      { position: newPosition, target: newTarget },
      transitionDurationSec,
      triggerOnce,
      undefined,
      false,
      'ease-in-out',
      false,
    );
    requestRender();
  };

  let previewTour = new PreviewTour({
    cameraDirector,
    clock,
    fov: config.camera.fov,
    startPosition: config.camera.previewTourStart ?? config.camera.initialPosition,
    endPosition: config.camera.previewTourEnd?.position,
    endTarget: config.camera.previewTourEnd?.target,
    enterDuration: config.camera.previewTourEnterDuration,
    exitDuration: config.camera.previewTourExitDuration,
  });

  let cameraFlyover = (!skipAnimations && config.camera.flyover)
    ? new CameraFlyover({ camera, controls, cameraDirector, config: config.camera.flyover })
    : null;

  let sightManager = new SightManager(
    camera,
    cameraDirector,
    controls,
    initialAppConfig.camera
  );

  // Forced-mode scenes (e.g. Mina) lock to a specific view mode. The preview
  // tour still runs in orbit for visual continuity; we flip to the forced mode
  // at the moment the tour terminates (see `applyForcedViewMode` below).
  const forcedViewMode = config.controls?.forcedViewMode;
  controls.setViewMode(forcedViewMode ? 'orbit' : $viewMode.getState());
  const stopViewModeWatch = $viewMode.watch((mode) => {
    if (forcedViewMode) return;
    controls.setViewMode(mode);
    requestRender();
  });
  /**
   * Switch into the scene-forced view mode after the preview-tour terminates.
   * Idempotent: safe to call multiple times. For FPS, also locks user-driven
   * movement so the user can only drag-to-look and POI-click.
   */
  const applyForcedViewMode = (): void => {
    if (!forcedViewMode) return;
    // Dev-override: позволить тюнить FPS-сцены в orbit-камере (panel toggle
    // "Disable Forced View Mode"). Применяется на следующем mount'е.
    if (currentDevConfig.disableForcedViewMode) return;
    if (controls.viewMode === forcedViewMode) return;
    if (forcedViewMode === 'first-person') {
      // Dev-override `unlockFpsMovement`: позволяем WASD/R/F/pinch-zoom
      // двигать камеру для тюнинга навигации в FPS-сценах.
      controls.firstPerson.lockMovement = !currentDevConfig.unlockFpsMovement;
    }
    controls.setViewMode(forcedViewMode);
    // Seed CameraDirector's lastPose* with FPS semantics (position + forward)
    // so the very first POI-click transition starts from a consistent pose.
    cameraDirector.syncPoseFromCamera();
    requestRender();
  };

  controls.onOrbitInteractionStart = () => {
    sightManager.stopOrbitAnimation();
    setOrbitAnimationEnabled(false);
    userInteracted();
    if (latestCameraConfig.orbitAutoMove) {
      updateCameraConfig({ orbitAutoMove: false });
    }
  };

  const orbitModeCenter = new THREE.Vector3();
  // Tracks the last orbit center value applied to controls. NaN sentinel guarantees
  // the first call always proceeds. Dedupe prevents stale config values from
  // overwriting a target that the user moved via right-click pan (OrbitControls
  // pan mutates controls.target directly without round-tripping through
  // cameraConfig.orbitCenter — re-applying the stale config value would snap
  // the camera back and trigger a jerk on every unrelated config update).
  let lastAppliedOrbitX = Number.NaN;
  let lastAppliedOrbitY = Number.NaN;
  let lastAppliedOrbitZ = Number.NaN;

  // Split orbit center between preview-tour phase and the main scene.
  //
  // When `previewModel.orbitCenter` is set, the active orbit center is the
  // preview value while the tour is mid-flight, and switches to
  // `controls.orbitCenter` (from app config) AFTER `previewTour.stop()` exit
  // transition completes — i.e. right BEFORE the splat reveal starts.
  //
  // The gating is on the *source* picked inside `updateOrbitCenter()`: every
  // call that fires during the preview phase (mount + any reactive $appConfig
  // / $devConfig update) writes the preview center, so dev-time mutations
  // can't snap `controls.target` away from the preview-tour anchor.
  //
  // For scenes that don't set `previewModel.orbitCenter`, `previewPhaseActive`
  // is false from the start — behavior is identical to the pre-split path.
  const previewOrbitCenterCfg = config.previewModel.orbitCenter ?? null;
  let previewPhaseActive = previewOrbitCenterCfg !== null && !skipAnimations;

  const updateOrbitCenter = (): void => {
    const source = (previewPhaseActive && previewOrbitCenterCfg)
      ? previewOrbitCenterCfg
      : (currentDevConfig.orbitCenterOverride ?? latestCameraConfig.orbitCenter);
    if (!source) {
      return;
    }
    if (
      source.x === lastAppliedOrbitX &&
      source.y === lastAppliedOrbitY &&
      source.z === lastAppliedOrbitZ
    ) {
      return;
    }
    lastAppliedOrbitX = source.x;
    lastAppliedOrbitY = source.y;
    lastAppliedOrbitZ = source.z;
    orbitModeCenter.set(source.x, source.y, source.z);
    controls.setOrbitCenter(orbitModeCenter);
    devVisuals.setOrbitCenter(orbitModeCenter);
    previewTour.setOrbitCenter(orbitModeCenter);
  };

  // Flip preview phase off + re-apply orbit center with the main source.
  // Must be called from every preview-tour terminator BEFORE splat reveal
  // starts (skipAnimations branch is exempt — it sets the flag to false
  // at construction). After tour exit transition, `controls.target` is
  // already at `previewTourEnd.target`; assuming the invariant
  // `previewTourEnd.target === controls.orbitCenter`, the follow-up
  // `controls.setOrbitCenter(mainCenter)` inside `updateOrbitCenter()`
  // is a no-op `setTarget` (no visible snap).
  const endPreviewPhase = (): void => {
    if (!previewPhaseActive) return;
    previewPhaseActive = false;
    updateOrbitCenter();
  };

  const applyCameraConfig = (config: CameraConfig): void => {
    latestCameraConfig = config;
    camera.fov = config.fov;
    camera.updateProjectionMatrix();
    controls.applyCameraConfig(config);
    sightManager.setCameraConfig(config);
    devVisuals.updateGridHeights(config);
    updateOrbitCenter();
    const {
      animate: animateViewOffset,
      animationDurationMs,
      ...viewOffset
    } = config.viewOffset;
    const viewOffsetAnimationDuration =
      typeof animationDurationMs === 'number'
        ? Math.max(0, animationDurationMs) / 1000
        : undefined;
    principalPointController.applyViewOffset(viewOffset, {
      animate: animateViewOffset ?? hasAppliedInitialCameraViewOffset,
      duration: viewOffsetAnimationDuration,
    });
    if (!hasAppliedInitialCameraViewOffset) {
      hasAppliedInitialCameraViewOffset = true;
    }

    // Re-position teller assistant when viewOffset changes (curtain open/close)
    updateTellerAssistantPosition();
  };

  const applyCollisionConfig = (config: CollisionConfig): void => {
    collisionSystem.updateConfig(config);
  };

  const applyCollidersConfig = (colliders: AppConfig['colliders']): void => {
    collisionSystem.setColliders(colliders);
    devVisuals.updateColliderVisuals(colliders);
    requestRender();
  };

  const applyDevConfig = (config: DevConfig): void => {
    const previousSkipPreloader = currentDevConfig.skipPreloaderAnimation;
    const previousUnlockConstraints = currentDevConfig.unlockCameraConstraints;
    currentDevConfig = config;
    // Runtime toggle support: if the dev flips `skipPreloaderAnimation`
    // mid-cinematic, collapse it on the spot via the same code path that
    // RestrictedCameraTuner.Unlock uses. Idempotent — late call is no-op.
    if (!previousSkipPreloader && config.skipPreloaderAnimation) {
      forceFinishPreloader();
    }
    // Live toggle for orbit-lock suspension. Init call has previous===new so
    // no-op — the init `suspendOrbitLocks()` (line ~1087) stays in effect
    // through the preloader cinematic regardless of the flag. Mid-session
    // change after preloader: flag flips → suspend or restore on the spot.
    if (previousUnlockConstraints !== config.unlockCameraConstraints) {
      if (config.unlockCameraConstraints) {
        controls.suspendOrbitLocks();
      } else {
        controls.restoreOrbitLocks();
      }
    }
    activeSparkRenderSettings = getDeviceRenderSettings(config, qualityProfile);
    sparkRenderer.blurAmount = activeSparkRenderSettings.blurAmount;
    sparkRenderer.preBlurAmount = activeSparkRenderSettings.preBlurAmount;
    sparkRenderer.maxStdDev = activeSparkRenderSettings.maxStdDev;
    sparkRenderer.minPixelRadius = activeSparkRenderSettings.minPixelRadius;
    sparkRenderer.maxPixelRadius = activeSparkRenderSettings.maxPixelRadius;
    sparkRenderer.autoUpdate = activeSparkRenderSettings.autoUpdate;
    sparkRenderer.premultipliedAlpha = activeSparkRenderSettings.premultipliedAlpha;
    sparkRenderer.preUpdate = activeSparkRenderSettings.preUpdate;
    sparkRenderer.originDistance = activeSparkRenderSettings.originDistance;
    sparkRenderer.minAlpha = activeSparkRenderSettings.minAlpha;
    sparkRenderer.enable2DGS = activeSparkRenderSettings.enable2DGS;
    sparkRenderer.focalDistance = activeSparkRenderSettings.focalDistance;
    sparkRenderer.apertureAngle = activeSparkRenderSettings.apertureAngle;
    sparkRenderer.falloff = activeSparkRenderSettings.falloff;
    sparkRenderer.clipXY = activeSparkRenderSettings.clipXY;
    sparkRenderer.focalAdjustment = activeSparkRenderSettings.focalAdjustment;
    const defaultView = (sparkRenderer as SparkRenderer & {
      defaultView?: {
        sortRadial?: boolean;
        sort32?: boolean;
        sortDistance?: number;
        sortCoorient?: boolean;
        depthBias?: number;
        sort360?: boolean;
        stochastic?: boolean;
      };
    }).defaultView;
    if (defaultView) {
      defaultView.sortRadial = activeSparkRenderSettings.sortRadial;
      defaultView.sort32 = activeSparkRenderSettings.sort32;
      defaultView.sortDistance = activeSparkRenderSettings.sortDistance;
      defaultView.sortCoorient = activeSparkRenderSettings.sortCoorient;
      defaultView.depthBias = activeSparkRenderSettings.depthBias;
      defaultView.sort360 = activeSparkRenderSettings.sort360;
      defaultView.stochastic = activeSparkRenderSettings.stochastic;
    }
    setRendererSize();
    markSparkDirty();
    devVisuals.applyDevConfig(config);
    if (config.showOrbitCenterMarker && devVisuals.currentGizmoTargetId === null) {
      devVisuals.setGizmoTarget(ORBIT_CENTER_GIZMO_ID);
    } else if (!config.showOrbitCenterMarker && devVisuals.currentGizmoTargetId === ORBIT_CENTER_GIZMO_ID) {
      devVisuals.setGizmoTarget(null);
    }
    if (config.showTellerAssistant) {
      if (tellerAssistant) {
        // Only make the assistant visible if the scene is fully revealed;
        // otherwise it will be enabled by splatAnimationTick after the reveal.
        tellerAssistant.setEnabled(sceneLoadingFinishedEmitted);
      } else if (createTellerAssistant) {
        void createTellerAssistant();
      }
    } else {
      tellerAssistant?.setEnabled(false);
    }
    rearViewModule.setEnabled(Boolean(config.showRearView));
    if (previewHiddenAfterReveal && previewModel) {
      if (config.debugSplatTransform || config.keepPreviewModel) {
        previewModel.show();
      } else {
        previewModel.hide();
      }
    }
    if (config.debugBboxes && !sceneBboxDebug) {
      sceneBboxDebug = new SceneBboxDebug({ scene });
      if (bboxDebugRefs.previewModel) sceneBboxDebug.attachPreviewModel(bboxDebugRefs.previewModel);
      if (bboxDebugRefs.splatMesh) sceneBboxDebug.attachSplatMesh(bboxDebugRefs.splatMesh);
    } else if (!config.debugBboxes && sceneBboxDebug) {
      sceneBboxDebug.dispose();
      sceneBboxDebug = null;
    }
    updateOrbitCenter();
  };

  const applyPoiConfig = (config: PoiConfig): void => {
    poiManager.setConfig(config);
  };

  /**
   * Update the teller assistant position based on current viewport and config.
   * The ball is anchored to the curtain (chat panel) top boundary via
   * viewOffset.bottom, with an additional verticalOffset from responsive config.
   * Also passes viewOffset.top so the shader can compensate for the camera's
   * principal-point shift (which would otherwise displace the ball on screen).
   */
  const updateTellerAssistantPosition = (): void => {
    if (!tellerAssistant) {
      return;
    }
    const responsiveConfig = isMobile
      ? latestTellerAssistantConfig.transform.mobile
      : latestTellerAssistantConfig.transform.desktop;

    const curtainBoundaryPx = latestCameraConfig.viewOffset.enabled
      ? latestCameraConfig.viewOffset.bottom
      : 0;

    const viewOffsetTopPx = latestCameraConfig.viewOffset.enabled
      ? (latestCameraConfig.viewOffset.top ?? 0)
      : 0;

    tellerAssistant.updatePositionFromViewport(
      canvasWidth,
      canvasHeight,
      latestCameraConfig.fov,
      isMobile,
      curtainBoundaryPx,
      viewOffsetTopPx,
      responsiveConfig
    );
  };

  const applyTellerAssistantConfig = (config: TellerAssistantConfig): void => {
    latestTellerAssistantConfig = config;
    if (!tellerAssistant) {
      return;
    }

    // Update position using responsive settings
    updateTellerAssistantPosition();

    // Update mesh params
    tellerAssistant.updateMeshParams({
      gooScale: config.mesh.gooScale,
      gooAmount: config.mesh.gooAmount,
      gooSpeed: config.mesh.gooSpeed,
      gooPoleAmount: config.mesh.gooPoleAmount,
      waveScale: config.mesh.waveScale,
      waveFrequency: config.mesh.waveFrequency,
      waveAmount: config.mesh.waveAmount,
      waveSpeed: config.mesh.waveSpeed,
      wavePoleAmount: config.mesh.wavePoleAmount,
      color: config.mesh.color,
      rotationSpeed: config.mesh.rotationSpeed,
      showNormals: config.mesh.showNormals,
      hoverAmplitude: config.transform.hoverAmplitude,
    });

    // Update shell params
    tellerAssistant.updateShellParams({
      particlesCount: config.shell.particlesCount,
      particlesColor: config.shell.particlesColor,
      particlesSize: config.shell.particlesSize,
      particlesRadius: config.shell.particlesRadius,
      particlesNoiseScale: config.shell.particlesNoiseScale,
      particlesNoiseSpeed: config.shell.particlesNoiseSpeed,
      particlesNoiseAmount: config.shell.particlesNoiseAmount,
    });

    // Update lighting
    tellerAssistant.updateLighting(config.lighting.lightingType, {
      lightX: config.lighting.simple.lightX,
      lightY: config.lighting.simple.lightY,
      lightZ: config.lighting.simple.lightZ,
      ambientLightColor: config.lighting.simple.ambientLightColor,
      directionalLightColor: config.lighting.simple.directionalLightColor,
      ambientLightIntensity: config.lighting.simple.ambientLightIntensity,
      directionalLightIntensity: config.lighting.simple.directionalLightIntensity,
      environmentLightingIntensity: config.lighting.env.environmentLightingIntensity,
      environmentRotationY: config.lighting.env.environmentRotationY,
    })
      .then(() => {
        requestRender();
      })
      .catch((error) => {
        console.error('Failed to update lighting:', error);
      });
  };

  const sceneControlsOverrides = resolvedControlsOverrides;
  const scenePoiOverrides = config.poi;

  const applyAppConfig = (appCfg: AppConfig): void => {
    applyCameraConfig(
      sceneControlsOverrides
        ? { ...appCfg.camera, ...sceneControlsOverrides }
        : appCfg.camera,
    );
    applyCollisionConfig(appCfg.collision);
    applyCollidersConfig(appCfg.colliders);
    applyDevConfig(appCfg.dev);
    applyPoiConfig(
      scenePoiOverrides
        ? { ...appCfg.poi, ...scenePoiOverrides }
        : appCfg.poi,
    );
    applyTellerAssistantConfig(appCfg.tellerAssistant);
  };

  initialAppConfig.camera.fov = config.camera.fov;
  applyAppConfig(initialAppConfig);

  // Suspend camera pose-restricting clamps for the duration of the
  // preloader animation (preview-tour enter/orbit/exit + flyover).
  // Locks are restored at `sceneLoadingFinished` (both call sites
  // below) so the user's configured azimuth/polar/distance/cage/
  // groundPlaneY take effect once the scene is interactive.
  // Authors of restricted-camera scenes (e.g. `overall`) can therefore
  // stage horizontal preview poses + arbitrary flyover paths without
  // the locks fighting the animation. Caveat: `previewTourEnd` /
  // final flyover segment must end in a pose compatible with the
  // configured locks, otherwise restoration causes a visible snap.
  controls.suspendOrbitLocks();

  const safeAreaInsets = $safeAreaInsets.getState();
  const viewportHeight = getViewportHeight();
  const initialHiddenOffset = getCurtainOffset('hidden', viewportHeight, 0, safeAreaInsets);
  updateCameraConfig({
    viewOffset: {
      enabled: true,
      top: 0,
      right: 0,
      left: 0,
      bottom: initialHiddenOffset,
      animate: false,
    },
  });

  const lastCameraPosition = camera.position.clone();
  const lastCameraQuaternion = camera.quaternion.clone();
  const cameraPositionThresholdSq = qualityProfile.cameraPositionThreshold ** 2;
  const cameraRotationThreshold = qualityProfile.cameraRotationThreshold;
  let isDisposed = false;
  // SceneBindings ref captured at onReady time so dispose can null its fields
  // IN PLACE and break external retention paths via Effector / React snapshots.
  // See "Etap 5" comment near `onReady?.(bindings)` below for full rationale.
  let bindingsForCleanup: SceneBindings | null = null;
  let animationFrameId: number | null = null;
  let timeoutId: number | null = null;
  let renderTier: RenderTier = 'active';
  let idleSinceMs: number | null = null;
  let renderMode: 'full' | 'assistant-only' = 'full';
  let assistantOnlyBackground: THREE.Color | THREE.Texture | null = null;
  let splatLoaded = false;
  let splatRevealDone = false;
  let sceneLoadingFinishedEmitted = false;
  let poiMarkersEnabledTime = Infinity;
  /**
   * Smoothly fade-out the preview-model's wave animation when splat reveal
   * starts. Two parallel mechanisms:
   *   1. `previewWaveTime` (fed to shader as `uTime`) ramps its growth rate
   *      from 1× to 0× over the freeze duration — stops new waves from being
   *      spawned and stops existing waves from propagating.
   *   2. `wavesColorIntensity` and `wavesDisplacement` ramp from their
   *      configured values to 0 over the same duration — kills the visible
   *      peak of any wave that was mid-cycle when freeze started, so the
   *      camera doesn't catch a "frozen splash" on screen.
   * The intensity ramp is the visually critical one (without it, ongoing
   * peaks stay visible at full amplitude even when time is frozen).
   * `previewFreezeStartTime` is null until reveal kicks in.
   */
  let previewWaveTime = 0;
  let previewFreezeStartTime: number | null = null;
  let previewFadeApplied = false;
  const previewWaveInitial = {
    colorIntensity: config.previewModel.style?.wavesColorIntensity ?? 5.0,
    displacement: config.previewModel.style?.wavesDisplacement ?? 0.01,
  };
  const PREVIEW_WAVE_FREEZE_DURATION = 0.3;
  const splatAnimationTime = dyno.dynoFloat(0);
  const splatRevealOrigin = dyno.dynoVec3([0, 0, 0] as [number, number, number]);
  const splatAnimationEndTime = config.splat.revealAnimationDuration;
  // Background-color reveal: when `environment.background.reveal` is set,
  // `scene.background` lerps from `background.color` (preloader tone) to
  // `reveal.color`. The lerp is driven by its OWN timer `bgRevealElapsed`
  // (incremented in `splatAnimationTick`, which only ticks once preview-tour
  // is complete and splat is loaded) — independent from `splatAnimationTime`
  // so `duration` can overshoot or undershoot `splat.revealAnimationDuration`.
  // Skipped (no allocation, no tick work) when `reveal` is absent OR when
  // `scene.background` is a Texture (skybox-textured scenes).
  const bgReveal = config.environment.background.reveal;
  const bgStartColor = bgReveal ? new THREE.Color(config.environment.background.color) : null;
  const bgRevealColor = bgReveal ? new THREE.Color(bgReveal.color) : null;
  const bgRevealDelay = bgReveal?.delay ?? 0;
  const bgRevealDuration = Math.max(bgReveal?.duration ?? splatAnimationEndTime, 0.001);
  let bgRevealElapsed = 0;
  let bgRevealDone = false;

  function markSparkDirty(): void {
    needsSparkUpdate = true;
    requestRender();
  }

  function requestImmediateSparkUpdate(): void {
    sparkRenderer.needsUpdate = true;
    markSparkDirty();
    sparkRenderer.update({
      scene,
      viewToWorld: camera.matrixWorld,
    });
  }

  function renderBaseSceneWithoutAssistant(): void {
    camera.layers.set(0);
    renderer.setClearColor(config.renderer.clearColor, 1);
    renderer.render(scene, camera);
  }

  function renderFullFrame(shouldRenderAssistant: boolean): void {
    renderBaseSceneWithoutAssistant();

    if (shouldRenderAssistant) {
      const backgroundBeforeAssistantPass = scene.background;
      const autoClearBeforeAssistantPass = renderer.autoClear;
      scene.background = null;
      camera.layers.set(ASSISTANT_RENDER_LAYER);
      renderer.setClearColor(0x000000, 0);
      renderer.autoClear = false;
      renderer.clearDepth();
      try {
        renderer.render(scene, camera);
      } finally {
        renderer.autoClear = autoClearBeforeAssistantPass;
        scene.background = backgroundBeforeAssistantPass;
      }
    }

    camera.layers.set(0);
    camera.layers.enable(ASSISTANT_RENDER_LAYER);
    renderer.setClearColor(config.renderer.clearColor, 1);
  }

  function restoreMainRenderState(): void {
    camera.layers.set(0);
    camera.layers.enable(ASSISTANT_RENDER_LAYER);
    scene.background = assistantOnlyBackground;
    assistantOnlyBackground = null;
    renderer.setClearColor(config.renderer.clearColor, 1);
    staticFrameOverlay.style.display = 'none';
    if (staticFrameOverlayContext) {
      staticFrameOverlayContext.clearRect(
        0,
        0,
        staticFrameOverlay.width,
        staticFrameOverlay.height
      );
    }
    sparkRenderer.needsUpdate = true;
    needsSparkUpdate = true;
  }

  function exitAssistantOnlyMode(): void {
    if (renderMode !== 'assistant-only') {
      return;
    }
    renderMode = 'full';
    restoreMainRenderState();
  }

  function enterAssistantOnlyMode(): boolean {
    if (renderMode === 'assistant-only') {
      return true;
    }
    if (!tellerAssistant || !tellerAssistant.needsRerender()) {
      return false;
    }

    try {
      renderBaseSceneWithoutAssistant();
      if (!staticFrameOverlayContext) {
        camera.layers.set(0);
        camera.layers.enable(ASSISTANT_RENDER_LAYER);
        renderer.setClearColor(config.renderer.clearColor, 1);
        return false;
      }
      staticFrameOverlayContext.clearRect(
        0,
        0,
        staticFrameOverlay.width,
        staticFrameOverlay.height
      );
      staticFrameOverlayContext.drawImage(renderer.domElement, 0, 0);
      staticFrameOverlay.style.display = 'block';
    } catch {
      camera.layers.set(0);
      camera.layers.enable(ASSISTANT_RENDER_LAYER);
      renderer.setClearColor(config.renderer.clearColor, 1);
      return false;
    }

    assistantOnlyBackground = scene.background;
    scene.background = null;
    camera.layers.set(ASSISTANT_RENDER_LAYER);
    renderer.setClearColor(0x000000, 0);
    renderer.render(scene, camera);
    renderMode = 'assistant-only';
    return true;
  }

  // splat selection + variant skybox setup hoisted earlier (before the
  // EnvironmentManager constructor) so the variant URL is the FIRST preload —
  // see "iOS OOM-relevant" comment near `resolveSplatSelection` above.
  let splatMesh: SplatMesh | null = null;
  sceneLoadingStarted();

  let previewModel = new PreviewModel({
    scene,
    options: {
      url: config.previewModel.url,
      transform: toPreviewTransform(config.previewModel.transform),
      onLoadProgress: sceneLoadingProgressed,
      style: {
        revealDistanceScale: config.splat.revealDistanceScale,
        revealUse3d: config.splat.revealMode === '3d',
        ...config.previewModel.style,
      },
    },
  });
  bboxDebugRefs.previewModel = previewModel;
  sceneBboxDebug?.attachPreviewModel(previewModel);

  // Initialize preview model config store with values from scene config
  const previewStyle = config.previewModel.style ?? {};
  updatePreviewModelConfig({
    wavesCenter: previewStyle.wavesCenter ?? { x: 0, y: 0, z: 0 },
    wavesMinDistance: previewStyle.wavesMinDistance ?? 0,
    wavesAmplitude: previewStyle.wavesAmplitude ?? 3.0,
    wavesPeriod: previewStyle.wavesPeriod ?? 1.0,
    wavesSpeed: previewStyle.wavesSpeed ?? 1.0,
    wavesColorIntensity: previewStyle.wavesColorIntensity ?? 5.0,
    wavesDisplacement: previewStyle.wavesDisplacement ?? 0.01,
  });

  const splatMeshPromise = currentDevConfig.keepPreviewModel
    ? previewModel.loadPromise.then(async () => {
        // Skip splat fetch entirely — just advance the state machine
        // so the UI pipeline (preloaderDone, hudReady) proceeds normally.
        await previewTour.stop();
        endPreviewPhase();
        controls.collisionSystem = collisionSystem;
        applyForcedViewMode();
        previewInputBlocker.style.display = 'none';
        splatFetchCompleted();
        splatInitCompleted();
        splatLoaded = true;
      })
    : (async () => {
        let fetchedBytes: Uint8Array | null = await loadWithCdnFallback(
          splatURL,
          (url) =>
            fetchBinaryWithProgress(url, (loaded, total) => {
              splatLoadingProgressed({ loadedBytes: loaded, totalBytes: total });
            }),
          { assetType: 'splat', sceneId: config.id },
        );
        // Diagnostic: track raw splat byte size — major iOS heap consumer until
        // SplatMesh decodes + frees it. Visible in mem-debug logs.
        logBigBuffer('splat fileBytes (post-fetch)', fetchedBytes.byteLength, {
          sceneId: config.id,
          url: splatURL,
        });
        splatFetchCompleted();

        await new Promise<void>((resolve, reject) => {
          // Non-null assertion: just successfully fetched, can't be null here.
          // We pass the buffer to SplatMesh which retains whatever it needs
          // internally (decoded packedSplats), then drop our outer reference
          // immediately after construction so the original Uint8Array can be
          // GC'd as soon as Spark finishes parsing.
          splatMesh = new SplatMesh({
            fileBytes: fetchedBytes!,
            fileName: splatURL,
            splatEncoding,
            onLoad: async (mesh) => {
              // Race guard: if React unmounted SceneCanvas after fetch
              // started but before the splat parsed (e.g. orderedSights
              // ref change re-triggered useEffect), dispose just-built mesh
              // and resolve — do NOT try to attach to a disposed scene.
              if (isDisposed) {
                try { mesh.dispose?.(); } catch { /* noop */ }
                resolve();
                return;
              }
              try {
                await initSplat(mesh);
                poiManager.createMarkers(allSights);
                resolve();
              } catch (error) {
                reject(error);
              }
            },
            objectModifier: skipAnimations ? undefined : dyno.dynoBlock(
              { gsplat: dyno.Gsplat },
              { gsplat: dyno.Gsplat },
              ({ gsplat }) => {
                // Get splat scale to convert revealDistanceScale from world to local coordinates
                const splatScale = splatVariant?.transform?.scale?.x
                  ?? config.splat.defaultTransform?.scale?.x
                  ?? 1.0;
                // revealDistanceScale is defined in world coordinates
                // For local coordinates: localDistanceScale = worldDistanceScale * splatScale
                const worldDistanceScale = config.splat.revealDistanceScale ?? 2.5;
                const localDistanceScale = worldDistanceScale * splatScale;

                const use3d = config.splat.revealMode === '3d';
                const d = new dyno.Dyno({
                  inTypes: { gsplat: dyno.Gsplat, t: "float", revealOriginLocal: "vec3", distanceScale: "float" },
                  outTypes: { gsplat: dyno.Gsplat },
                  statements: ({ inputs, outputs }) => dyno.unindentLines(`
                ${outputs.gsplat} = ${inputs.gsplat};
                float t = ${inputs.t};
                vec3 localPos = ${inputs.gsplat}.center;
                vec3 offsetFromOrigin = localPos - ${inputs.revealOriginLocal};
                float l = ${use3d ? 'length(offsetFromOrigin)' : 'length(offsetFromOrigin.xz)'};
                float tttt = pow(t, 4.0)*.4+.5;
                float visibility = clamp(tttt - l * ${inputs.distanceScale}, 0., 1.);
                vec4 originalRgba = ${inputs.gsplat}.rgba;
                ${outputs.gsplat}.rgba = vec4(originalRgba.rgb, originalRgba.a * visibility);
              `),
                });

                gsplat = d.apply({
                  gsplat,
                  t: splatAnimationTime,
                  revealOriginLocal: splatRevealOrigin,
                  distanceScale: dyno.dynoFloat(localDistanceScale),
                }).gsplat;

                return { gsplat };
              }
            )
          });

          // Race guard (synchronous path right after constructor): scene
          // may have been nulled by IIFE-locals cleanup (rule #14) between
          // the prior `await fetch` and now.
          if (isDisposed) {
            try { splatMesh.dispose?.(); } catch { /* noop */ }
            resolve();
            return;
          }
          splatMesh.position.set(0, 0, 0);
          scene.add(splatMesh);
          markSparkDirty();
        });
        // Drop our own strong reference to the raw splat bytes once SplatMesh
        // has consumed them. Without this, the Uint8Array (28–50 MB for a
        // 1–2M-splat scene) stays pinned by the IIFE closure until the closure
        // itself is collected — which on iOS JSC can lag for seconds across
        // scene transitions. Spark internally retains whatever bytes it needs
        // (PackedSplats decoded form), so this is safe.
        fetchedBytes = null;
      })();

  const onFlyoverSkipInteraction = (): void => {
    if (cameraFlyover && !cameraFlyover.completed) {
      cameraFlyover.skipSmooth();
      cameraPreloaderSkipped();
    }
    removeFlyoverSkipListeners();
  };

  function addFlyoverSkipListeners(): void {
    if (!cameraFlyover) {
      return;
    }
    // Skip listeners live on `previewInputBlocker` (NOT canvas) because the
    // blocker stays on top of the canvas through the entire preloader
    // (preview-tour → flyover → splat reveal) to prevent the user from
    // moving the camera during the post-flyover-but-pre-reveal-end window.
    // Events fire on the blocker first; canvas never sees them.
    previewInputBlocker.addEventListener('pointerdown', onFlyoverSkipInteraction);
    previewInputBlocker.addEventListener('wheel', onFlyoverSkipInteraction);
  }

  function removeFlyoverSkipListeners(): void {
    previewInputBlocker.removeEventListener('pointerdown', onFlyoverSkipInteraction);
    previewInputBlocker.removeEventListener('wheel', onFlyoverSkipInteraction);
  }

  async function initSplat(mesh: SplatMesh): Promise<void> {
    // We hide mesh until camera is stopped, because we use camera position as reveal effect origin.
    mesh.visible = false;

    const delaySec = currentDevConfig.previewLoadDelaySec;
    if (delaySec > 0 && !skipAnimations) {
      await new Promise<void>(resolve => setTimeout(resolve, delaySec * 1000));
    }

    applySplatTransform(mesh, config.splat.defaultTransform, splatVariant);
    initSplatTransform({
      position: { x: mesh.position.x, y: mesh.position.y, z: mesh.position.z },
      rotation: { x: mesh.rotation.x, y: mesh.rotation.y, z: mesh.rotation.z },
      scale: { x: mesh.scale.x, y: mesh.scale.y, z: mesh.scale.z },
    });
    bboxDebugRefs.splatMesh = mesh;
    sceneBboxDebug?.attachSplatMesh(mesh);

    if (skipAnimations) {
      // Instant finalization: no reveal shader, no flyover, no preview tour
      previewTour.complete();
      controls.collisionSystem = collisionSystem;
      applyForcedViewMode();
      previewInputBlocker.style.display = 'none';

      // Caller-provided pose (scene-state restore on goBack from inside-scene)
      // takes precedence over the calculated flyover-tail / previewTourEnd
      // fallback. Without this override we'd land at the scene's default
      // entry framing rather than where the user actually left the scene.
      if (initialCameraPose) {
        cameraDirector.moveToPose({
          position: new THREE.Vector3(
            initialCameraPose.position.x,
            initialCameraPose.position.y,
            initialCameraPose.position.z,
          ),
          target: new THREE.Vector3(
            initialCameraPose.target.x,
            initialCameraPose.target.y,
            initialCameraPose.target.z,
          ),
          fov: initialCameraPose.fov,
        });
        mesh.visible = true;
        splatLoaded = true;
        requestImmediateSparkUpdate();
        splatInitCompleted();
        environmentManager.setMainBackground(true);

        if (bgRevealColor && scene.background instanceof THREE.Color) {
          scene.background.copy(bgRevealColor);
          bgRevealDone = true;
        }

        splatRevealDone = true;
        sceneLoadingFinishedEmitted = true;
        if (!currentDevConfig.keepPreviewModel && !currentDevConfig.debugSplatTransform) {
          previewModel?.hide();
        }
        previewHiddenAfterReveal = true;
        poiMarkersEnabledTime = performance.now() + HUD_REVEAL_DELAY;
        if (!currentDevConfig.unlockCameraConstraints) {
          controls.restoreOrbitLocks();
        }
        sceneLoadingFinished();
        if (tellerAssistant && currentDevConfig.showTellerAssistant) {
          tellerAssistant.setEnabled(true);
        }
        return;
      }

      // Position camera at flyover endpoint (or previewTourEnd fallback)
      const flyoverSegs = config.camera.flyover?.segments;
      if (flyoverSegs?.length) {
        const startPos = config.camera.previewTourEnd?.position ?? config.camera.initialPosition;
        let curX = startPos.x;
        let curZ = startPos.z;
        let finalPose: CameraPose | null = null;

        for (const seg of flyoverSegs) {
          if (seg.type === 'waypoint') {
            curX = seg.position.x;
            curZ = seg.position.z;
            finalPose = {
              position: new THREE.Vector3(seg.position.x, seg.position.y, seg.position.z),
              target: new THREE.Vector3(seg.target.x, seg.target.y, seg.target.z),
              fov: seg.fov,
            };
          } else {
            const az = Math.atan2(curZ - seg.center.z, curX - seg.center.x);
            const endAz = az + seg.angleDeg * (Math.PI / 180);
            curX = seg.center.x + seg.radius * Math.cos(endAz);
            curZ = seg.center.z + seg.radius * Math.sin(endAz);
            finalPose = {
              position: new THREE.Vector3(curX, seg.center.y + seg.height, curZ),
              target: new THREE.Vector3(seg.center.x, seg.center.y, seg.center.z),
              fov: seg.fov,
            };
          }
        }
        if (finalPose) cameraDirector.moveToPose(finalPose);
      } else if (config.camera.previewTourEnd) {
        const end = config.camera.previewTourEnd;
        cameraDirector.moveToPose({
          position: new THREE.Vector3(end.position.x, end.position.y, end.position.z),
          target: new THREE.Vector3(end.target.x, end.target.y, end.target.z),
        });
      }

      mesh.visible = true;
      splatLoaded = true;
      requestImmediateSparkUpdate();
      splatInitCompleted();
      environmentManager.setMainBackground(true);

      // No reveal animation runs in this branch — snap background to its
      // final color immediately and mark the lerp done so subsequent
      // splatAnimationTick frames skip the no-op color copy.
      if (bgRevealColor && scene.background instanceof THREE.Color) {
        scene.background.copy(bgRevealColor);
        bgRevealDone = true;
      }

      splatRevealDone = true;
      sceneLoadingFinishedEmitted = true;
      if (!currentDevConfig.keepPreviewModel && !currentDevConfig.debugSplatTransform) {
        previewModel?.hide();
      }
      previewHiddenAfterReveal = true;
      poiMarkersEnabledTime = performance.now() + HUD_REVEAL_DELAY;
      // Re-enable pose locks (azimuth/polar/distance/cage/groundPlaneY)
      // now that preloader animation is over. Camera should already be at
      // `previewTourEnd` (a lock-compatible pose) — no snap.
      // Dev `unlockCameraConstraints` keeps locks suspended for free-fly.
      if (!currentDevConfig.unlockCameraConstraints) {
        controls.restoreOrbitLocks();
      }
      sceneLoadingFinished();
      if (tellerAssistant && currentDevConfig.showTellerAssistant) {
        tellerAssistant.setEnabled(true);
      }
      return;
    }

    await previewTour.stop();
    // Switch active orbit center from `previewModel.orbitCenter` to
    // `controls.orbitCenter` (main scene). Must happen BEFORE splat reveal
    // starts so the reveal animation plays with the correct orbit anchor.
    endPreviewPhase();
    controls.collisionSystem = collisionSystem;
    applyForcedViewMode();
    // NOTE: `previewInputBlocker` stays visible through the entire preloader
    // (preview-tour → flyover → splat reveal). It is hidden only when
    // `sceneLoadingFinished()` fires in `splatAnimationTick`, i.e. AFTER both
    // the splat reveal shader animation AND the flyover have completed.
    // Without this, the post-flyover-but-pre-reveal-end window leaves
    // `controls.enabled = true` (flyover restores it on `complete()`) while
    // the reveal shader is still playing — the user could rotate / zoom /
    // pan during the cinematic. Skip listeners are now attached to the
    // blocker (see `addFlyoverSkipListeners`) so clicks still skip flyover.

    // Compute reveal origin in mesh local coordinates (must be after applySplatTransform)
    const revealOriginWorld = config.splat.revealOrigin
      ? new THREE.Vector3(config.splat.revealOrigin.x, config.splat.revealOrigin.y, config.splat.revealOrigin.z)
      : camera.position.clone();
    const revealOriginLocal = revealOriginWorld.clone();
    mesh.updateMatrixWorld(true);
    revealOriginLocal.applyMatrix4(mesh.matrixWorld.clone().invert());
    splatRevealOrigin.value = [revealOriginLocal.x, revealOriginLocal.y, revealOriginLocal.z];

    previewModel?.setRevealOrigin(revealOriginWorld);
    mesh.visible = !currentDevConfig.keepPreviewModel;
    splatLoaded = true;
    requestImmediateSparkUpdate();
    splatInitCompleted();

    // Show skybox early — fade in parallel with splat reveal
    environmentManager.setMainBackground();

    // Start flyover in parallel with splat reveal animation.
    // Dev-override: при `unlockCameraDuringPreloader` flyover не запускается,
    // чтобы он не дёргал камеру (внутри start() он ставит controls.enabled=false).
    if (!currentDevConfig.unlockCameraDuringPreloader) {
      cameraFlyover?.start();
      addFlyoverSkipListeners();
    }
  }

  createTellerAssistant = async (): Promise<void> => {
    if (tellerAssistant || isDisposed) {
      return;
    }

    const responsiveConfig = isMobile
      ? latestTellerAssistantConfig.transform.mobile
      : latestTellerAssistantConfig.transform.desktop;

    tellerAssistant = new TellerAssistantShader({
      anchor: camera,
      scene,
      renderer,
      position: latestTellerAssistantConfig.transform.position,
      rotationY: latestTellerAssistantConfig.transform.rotationY,
      scale: responsiveConfig.scale,
      useEnvironmentLighting: latestTellerAssistantConfig.lighting.lightingType === 'env',
      environmentLightingIntensity: latestTellerAssistantConfig.lighting.env.environmentLightingIntensity,
      panoramaTextureUrl: resolveCdnUrl('environment/sky.hdr'),
      isMobile,
    });
    tellerAssistant.setRenderLayer(ASSISTANT_RENDER_LAYER);

    await tellerAssistant.load(splatMeshPromise);
    updateTellerAssistantPosition();
    applyTellerAssistantConfig(latestTellerAssistantConfig);
    // Don't enable immediately — the teller assistant is shown only after
    // the scene reveal animation completes (see splatAnimationTick).
    // If the scene is already fully revealed (lazy creation via dev tools),
    // enable right away.
    tellerAssistant.setEnabled(sceneLoadingFinishedEmitted);
    requestRender();
  };

  // Only create the teller assistant at startup if it is enabled in the dev config;
  // otherwise it will be created lazily when first enabled at runtime.
  const tellerAssistantPromise = currentDevConfig.showTellerAssistant
    ? createTellerAssistant()
    : Promise.resolve();

  previewModel.loadPromise
    .then(() => {
      scenePreviewLoadingFinished();
      // Dev-override `unlockCameraDuringPreloader`: пропускаем показ
      // блокировщика и старт preview-tour, чтобы юзер имел свободную
      // камеру всю прелоадер-фазу для тюнинга поз. Маркируем preview-tour
      // как completed — иначе `await previewTour.stop()` в initSplat запустил
      // бы pose-transition и перебил бы камеру юзера.
      if (currentDevConfig.unlockCameraDuringPreloader) {
        previewTour.complete();
      }
      if (!skipAnimations && !currentDevConfig.unlockCameraDuringPreloader) {
        // Show transparent overlay swallowing pointer/touch/wheel — keeps
        // OrbitControls.update() running (orbitAutoMove plays) while ignoring
        // every user input. Cannot use `controls.enabled = false` here because
        // SceneControls.update() early-returns when disabled, which would
        // freeze the orbital fly-around (PreviewTour relies on orbitAutoMove,
        // see PreviewTour.start). The blocker now stays visible through the
        // entire preloader (preview-tour → flyover → splat reveal); hidden
        // only when `sceneLoadingFinished` fires in `splatAnimationTick`,
        // or via early-out terminators (skipAnimations / keepPreviewModel
        // dev shortcut / forceFinishPreloader). Skip listeners are attached
        // to the blocker (see `addFlyoverSkipListeners`).
        previewInputBlocker.style.display = 'block';
        previewTour.start();
      }
      requestRender();
    })
    .catch((error) => {
      console.error('Scene asset loading failed', error);
      sceneLoadingFailed({ stage: 'preview', error });
    });

  Promise.all([splatMeshPromise, tellerAssistantPromise])
    .catch((error) => {
      console.error('Scene asset loading failed', error);
      sceneLoadingFailed({ stage: 'splat', error });
    });

  if (isDevMode) {
    devVisuals.addSightDebugMarkers(allSights);
    devVisuals.applyDevConfig(currentDevConfig);
  }

  const stopConfigWatch = $appConfig.watch((nextConfig) => {
    applyAppConfig(nextConfig);
    requestRender();
  });
  const stopPreviewModelConfigWatch = previewModel
    ? $previewModelConfig.watch((config) => {
        previewModel.updateWavesConfig(config);
        requestRender();
      })
    : () => {};
  let isInitialBackgroundConfig = true;
  const stopBackgroundConfigWatch = $backgroundConfig.watch((bgConfig) => {
    // Skip initial subscription call to preserve preview color from setupBackground()
    if (isInitialBackgroundConfig) {
      isInitialBackgroundConfig = false;
      return;
    }
    environmentManager.setBackgroundColor(bgConfig.color);
    environmentManager.setSkyboxUrl(bgConfig.skyboxUrl);
    environmentManager.setSkyboxEnabled(bgConfig.skyboxEnabled);
    if (bgConfig.skyboxEnabled) {
      environmentManager.setSkyboxIntensity(bgConfig.skyboxIntensity);
      environmentManager.setSkyboxBlur(bgConfig.skyboxBlur);
      environmentManager.setSkyboxRotation(bgConfig.skyboxRotation);
    }
    requestRender();
  });
  const stopTellerAssistantConfigWatch = $tellerAssistantConfig.watch((config) => {
    applyTellerAssistantConfig(config);
    requestRender();
  });
  const stopFocusedSightWatch = $currentSight.watch((sight) => {
    poiManager.setFocusedSightId(sight?.id ?? null);
    requestRender();
  });
  const stopOrbitAnimationWatch = $orbitAnimationEnabled.watch((enabled) => {
    const sight = $currentSight.getState();
    sightManager.setOrbitAnimationEnabled(enabled);
    if (enabled && sight && !sightManager.isInTransition()) {
      sightManager.startOrbitAnimationForSight(sight);
    } else {
      sightManager.stopOrbitAnimation();
    }
    requestRender();
  });
  const stopOrbitSightWatch = $currentSight.watch((sight) => {
    if (!$orbitAnimationEnabled.getState()) {
      return;
    }
    sightManager.setOrbitAnimationEnabled(true);
    if (sight && !sightManager.isInTransition()) {
      sightManager.startOrbitAnimationForSight(sight);
    } else {
      sightManager.stopOrbitAnimation();
    }
    requestRender();
  });
  const stopGizmoWatch = $gizmoTargetId.watch((id) => {
    devVisuals.setGizmoTarget(id);
    requestRender();
  });

  const stopOrbitTargetOverrideWatch = clearOrbitTargetOverride.watch(() => {
    sightManager.stopOrbitAnimation();
    controls.clearOrbitTargetOverride();
    requestRender();
  });
  const stopSplatTransformWatch = $splatTransform.watch((transform) => {
    if (!splatMesh) return;
    splatMesh.position.set(transform.position.x, transform.position.y, transform.position.z);
    splatMesh.rotation.set(transform.rotation.x, transform.rotation.y, transform.rotation.z);
    splatMesh.scale.set(transform.scale.x, transform.scale.y, transform.scale.z);
    requestImmediateSparkUpdate();
    sceneBboxDebug?.refreshSplatBbox();
  });
  const stopSplatVisibleWatch = $splatDebugVisible.watch((visible) => {
    if (!splatMesh) return;
    splatMesh.visible = visible;
    requestImmediateSparkUpdate();
  });

  // Etap 5 (iOS heap leak — KILLER fix):
  //
  // The SceneBindings object is passed via Effector's `sceneReady` event into
  // the `$sceneBindings` store. Even after `$sceneBindings.reset(...)` zeroes
  // the store value, Effector subscribers (sample source-readers, useUnit
  // hooks in dev panels) and React fiber prev-state can retain stale
  // references to the ORIGINAL bindings object. Each retained reference
  // pins the entire scene infrastructure (scene + camera + sparkRenderer +
  // controls + sightManager + tellerAssistant — all listed as fields here).
  //
  // Retention probes confirmed this: at +9s after dispose, splatApi.dispose
  // === NOOP (closure broken) but scene / camera / sparkRenderer / splatMesh
  // are still alive. Their only common reachability path is via this object.
  //
  // Solution: keep a reference here so dispose can NULL the fields IN PLACE.
  // Whoever still holds the bindings object will keep it (it's small — 8
  // pointer slots), but its fields no longer reach the heavy infrastructure.
  // GC then collects scene / camera / sparkRenderer independently.
  // Dev-time escape hatch for RestrictedCameraTuner's "Unlock camera" button.
  // Restricted top-down scenes hide a transparent `previewInputBlocker`
  // overlay during the preloader cinematic (preview-tour auto-rotate +
  // optional flyover) so user input doesn't fight the cinematic. Without
  // this method, clicking Unlock while the preloader is still running
  // succeeds at the wrapper-state level (panMode / cage / polar all open)
  // but visually does nothing: the blocker keeps pointer/wheel events away
  // from the canvas, `_locksSuspended` is still `true`, and the running
  // `transitionToPose` keeps writing camera position every frame.
  //
  // `forceFinishPreloader()` collapses the cinematic on the spot:
  //   1. cancel any in-flight CameraDirector transition (clears its
  //      onComplete which would have flipped `orbitAutoMove` back to true);
  //   2. mark preview-tour completed (sync — disables autoMove);
  //   3. mark flyover completed (sync snap to final pose);
  //   4. detach flyover-skip listeners;
  //   5. restore orbit locks if still suspended;
  //   6. wire collisionSystem + forced view mode (mirrors what the
  //      `await previewTour.stop()` path does on the regular timeline);
  //   7. hide the input blocker.
  //
  // Every step is idempotent — late callers (Unlock pressed after the
  // preloader already finished) are a no-op. After this runs, the normal
  // `previewModel.loadPromise.then(...)` continuation finds:
  //   - `previewTour.stop()` returns a pre-resolved promise (early-return
  //     guard on `_completed`);
  //   - `cameraFlyover.start()` returns a pre-resolved promise (same guard);
  // so the rest of the loading path (reveal animation, POI markers, etc.)
  // still runs but never touches the user's free-fly camera.
  //
  // Assigned to the forward-declared `forceFinishPreloader` let so the
  // `applyDevConfig` watcher (defined earlier — see ~line 715) can call it
  // for the runtime `skipPreloaderAnimation` toggle.
  forceFinishPreloader = (): void => {
    const wasInPreloader =
      (cameraFlyover && !cameraFlyover.completed) ||
      (previewTour && !previewTour.completed);
    cameraDirector.cancelTransition();
    previewTour?.complete();
    endPreviewPhase();
    if (cameraFlyover && !cameraFlyover.completed) {
      cameraFlyover.skip();
    }
    removeFlyoverSkipListeners();
    if (controls.orbit.locksSuspended && !currentDevConfig.unlockCameraConstraints) {
      controls.restoreOrbitLocks();
    }
    controls.collisionSystem = collisionSystem;
    applyForcedViewMode();
    previewInputBlocker.style.display = 'none';
    if (wasInPreloader) cameraPreloaderSkipped();
  };

  /**
   * Reads camera position + look-target + fov as plain numbers. Used by
   * `saveCurrentSceneSnapshot` (scene-state persistence on entry into an
   * inside-scene) — the snapshot is later replayed via `initialCameraPose`
   * on return. After dispose this is replaced with `EMPTY_CAMERA_POSE_GETTER`
   * so callers don't have to null-check.
   *
   * Target derivation depends on view mode: orbit mode reads
   * `controls.orbit.target` directly (authoritative); FPS mode derives
   * a virtual target one unit ahead of the look direction (parity with
   * `CameraDirector.syncPoseFromCamera`).
   *
   * Declared here (right before `bindings`) so the SceneBindings object
   * literal below can reference it without hitting TDZ. The dispose
   * codepath overwrites `api.getCurrentCameraPose` + `bindings.getCurrentCameraPose`
   * with `EMPTY_CAMERA_POSE_GETTER` to sever the closure chain.
   */
  const getCurrentCameraPose = (): SplatCameraPose => {
    if (controls.viewMode === 'orbit') {
      return {
        position: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
        target: {
          x: controls.orbit.target.x,
          y: controls.orbit.target.y,
          z: controls.orbit.target.z,
        },
        fov: camera.fov,
      };
    }
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
    const tgt = camera.position.clone().add(fwd);
    return {
      position: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
      target: { x: tgt.x, y: tgt.y, z: tgt.z },
      fov: camera.fov,
    };
  };

  const bindings: SceneBindings = {
    camera,
    controls,
    sightManager,
    renderer,
    sparkRenderer,
    scene,
    getPoiMarkerDiagnostics,
    getCurrentCameraPose,
    tellerAssistant,
    forceFinishPreloader,
    navPoiTuner,
  };
  bindingsForCleanup = bindings;
  onReady?.(bindings);

  function onResize(): void {
    if (isPaused) return;
    setRendererSize();
    controls.handleResize();
    // Skip instant re-apply while camera offset is animating — the animation
    // target is already correct. ResizeObserver fires on every CSS transition
    // frame of canvas bottom, and an instant applyViewOffset would kill the
    // ongoing smooth animation causing a visible camera jump.
    if (!principalPointController.isAnimating()) {
      principalPointController.applyViewOffset(latestCameraConfig.viewOffset, { animate: false });
    }
    principalPointController.forceSync();
    rearViewModule.handleResize();
    updateTellerAssistantPosition();
    markSparkDirty();
  }

  function onUserInteraction(): void {
    requestRender();
  }

  function onKeyInteraction(event: KeyboardEvent): void {
    if (!KEYBOARD_RENDER_WAKE_CODES.has(event.code)) {
      return;
    }
    if (controls.viewMode !== 'first-person' || !controls.enabled) {
      return;
    }
    if (isEditableTarget(event.target)) {
      return;
    }

    const target = event.target;
    if (
      target instanceof Node &&
      target !== document.body &&
      !container.contains(target)
    ) {
      return;
    }

    requestRender();
  }

  function onVisibilityChange(): void {
    if (document.hidden) {
      exitAssistantOnlyMode();
      renderTier = 'paused';
      idleSinceMs = null;
      cancelScheduledFrames();
      return;
    }
    requestRender();
  }

  function trackCameraMovement(): boolean {
    let cameraMoved = false;

    if (lastCameraPosition.distanceToSquared(camera.position) > cameraPositionThresholdSq) {
      lastCameraPosition.copy(camera.position);
      cameraMoved = true;
    }

    const quaternionDelta = 1 - Math.abs(lastCameraQuaternion.dot(camera.quaternion));
    if (quaternionDelta > cameraRotationThreshold) {
      lastCameraQuaternion.copy(camera.quaternion);
      cameraMoved = true;
    }

    if (cameraMoved) {
      needsSparkUpdate = true;
    }

    return cameraMoved;
  }

  function splatAnimationTick(deltaT: number): boolean {
    if (!splatLoaded || !previewTour.completed) {
      return false;
    }

    // Background lerp — its own clock, can outlast or finish before the
    // splat-reveal shader animation. Skipped after `bgRevealDone` flips
    // to avoid per-frame Color.copy work for no visible change.
    if (
      !bgRevealDone
      && bgStartColor
      && bgRevealColor
      && scene.background instanceof THREE.Color
    ) {
      bgRevealElapsed += deltaT;
      const t = (bgRevealElapsed - bgRevealDelay) / bgRevealDuration;
      const progress = THREE.MathUtils.clamp(t, 0, 1);
      scene.background.copy(bgStartColor).lerp(bgRevealColor, progress);
      if (progress >= 1) {
        bgRevealDone = true;
      }
    }

    const flyoverAnimating = cameraFlyover?.update(deltaT) ?? false;

    if (splatAnimationTime.value >= splatAnimationEndTime) {
      if (!splatRevealDone) {
        splatRevealDone = true;
        previewHiddenAfterReveal = true;
        if (!currentDevConfig.keepPreviewModel && !currentDevConfig.debugSplatTransform) {
          previewModel?.hide();
        }
      }

      const flyoverDone = !cameraFlyover || cameraFlyover.completed;

      if (!sceneLoadingFinishedEmitted && flyoverDone) {
        // First frame after reveal + flyover: notify UI.
        removeFlyoverSkipListeners();
        // Hide the input blocker now that the entire preloader (preview-
        // tour + flyover + splat reveal) is fully complete. Blocker has
        // been swallowing user input throughout to keep the cinematic
        // un-interruptible by camera moves.
        previewInputBlocker.style.display = 'none';
        sceneLoadingFinishedEmitted = true;
        poiMarkersEnabledTime = performance.now() + HUD_REVEAL_DELAY;
        // Re-enable pose locks now that the cinematic preloader
        // animation is fully complete. Final flyover segment / preview-
        // tour exit pose should already match locks → no snap.
        // Dev `unlockCameraConstraints` keeps locks suspended for free-fly.
        if (!currentDevConfig.unlockCameraConstraints) {
          controls.restoreOrbitLocks();
        }
        sceneLoadingFinished();
        // Show teller assistant now that the scene is fully revealed
        if (tellerAssistant && currentDevConfig.showTellerAssistant) {
          tellerAssistant.setEnabled(true);
        }
        return true;
      } else if (!flyoverDone) {
        // Splat reveal done but flyover still running (or skip transition in progress) — keep rendering
        return true;
      } else if (splatMesh?.objectModifier) {
        // Next frame: remove the now-redundant modifier so the GPU shader
        // no longer runs on every splat. Deferred by one frame to avoid
        // a heavy updateGenerator() call on the same frame as dispose.
        splatMesh.objectModifier = undefined;
        splatMesh.updateGenerator();
        sparkRenderer.needsUpdate = true;
        needsSparkUpdate = true;
        return true;
      }
      return flyoverAnimating;
    }

    splatAnimationTime.value += deltaT;
    sparkRenderer.needsUpdate = true;
    needsSparkUpdate = true;
    return true;
  }

  function isFrameDirty({
    sparkDirty,
    cameraMovedThisFrame,
    splatAnimating,
    previewAnimating,
    wasPrincipalPointAnimating,
    wasSightTransitioning,
    environmentAnimating,
  }: {
    sparkDirty: boolean;
    cameraMovedThisFrame: boolean;
    splatAnimating: boolean;
    previewAnimating: boolean;
    wasPrincipalPointAnimating: boolean;
    wasSightTransitioning: boolean;
    environmentAnimating: boolean;
  }): boolean {
    return (
      sparkDirty ||
      cameraMovedThisFrame ||
      splatAnimating ||
      previewAnimating ||
      wasPrincipalPointAnimating ||
      principalPointController.isAnimating() ||
      wasSightTransitioning ||
      sightManager.isInTransition() ||
      environmentAnimating
    );
  }

  function updateRenderTier(frameDirty: boolean, nowMs: number): void {
    if (frameDirty) {
      idleSinceMs = null;
      renderTier = 'active';
      return;
    }

    if (idleSinceMs === null) {
      idleSinceMs = nowMs;
    }

    const idleMs = nowMs - idleSinceMs;
    if (idleMs >= IDLE_RENDER_PAUSED_THRESHOLD_MS) {
      renderTier = 'paused';
      return;
    }
    if (idleMs >= IDLE_RENDER_MINIMAL_THRESHOLD_MS) {
      renderTier = 'minimal';
      return;
    }
    if (idleMs >= IDLE_RENDER_LOW_THRESHOLD_MS) {
      renderTier = 'low';
      return;
    }
    renderTier = 'active';
  }

  function scheduleNextFrame(): void {
    if (isDisposed || animationFrameId !== null) {
      return;
    }

    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    switch (renderTier) {
      case 'active':
        animationFrameId = requestAnimationFrame(renderLoop);
        break;
      case 'low':
        timeoutId = window.setTimeout(() => {
          timeoutId = null;
          if (!isDisposed && animationFrameId === null) {
            animationFrameId = requestAnimationFrame(renderLoop);
          }
        }, IDLE_RENDER_LOW_DELAY_MS);
        break;
      case 'minimal':
        timeoutId = window.setTimeout(() => {
          timeoutId = null;
          if (!isDisposed && animationFrameId === null) {
            animationFrameId = requestAnimationFrame(renderLoop);
          }
        }, IDLE_RENDER_MINIMAL_DELAY_MS);
        break;
      case 'paused':
        break;
    }
  }

  function cancelScheduledFrames(): void {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  }

  function requestRender(): void {
    if (!renderSchedulerReady || isDisposed || document.hidden) {
      return;
    }

    exitAssistantOnlyMode();
    renderTier = 'active';
    idleSinceMs = null;

    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    if (animationFrameId === null) {
      animationFrameId = requestAnimationFrame(renderLoop);
    }
  }

  function renderLoop(): void {
    animationFrameId = null;
    if (isDisposed) {
      return;
    }
    stats?.begin();

    // Clamp delta after a paused period to avoid animation jumps.
    const delta = Math.min(clock.getDelta(), MAX_FRAME_DELTA_SECONDS);
    const currentTime = clock.getElapsedTime();
    const tellerAnimating = tellerAssistant?.needsRerender() ?? false;

    if (renderMode === 'assistant-only') {
      if (!tellerAnimating) {
        exitAssistantOnlyMode();
        updateRenderTier(false, performance.now());
        scheduleNextFrame();
        return;
      }

      tellerAssistant?.update(delta, camera);
      camera.updateMatrixWorld(true);
      renderer.render(scene, camera);
      renderTier = 'active';
      idleSinceMs = null;
      stats?.end();
      scheduleNextFrame();
      return;
    }

    const wasPrincipalPointAnimating = principalPointController.isAnimating();
    const wasSightTransitioning = sightManager.isInTransition();

    sightManager.update(currentTime);
    controls.update(delta);
    if (!isOrbitCenterGizmoDragging) {
      devVisuals.syncOrbitCenterMarker(controls.orbit.target);
    }
    cameraDirector.syncPoseFromCamera();
    if (tellerAnimating) {
      tellerAssistant?.update(delta, camera);
    }
    const cameraMovedThisFrame = trackCameraMovement();
    camera.updateMatrixWorld(true);
    principalPointController.update(currentTime);
    camera.updateProjectionMatrix();
    if (performance.now() >= poiMarkersEnabledTime) {
      poiManager.update(camera, canvasWidth, canvasHeight);
    }
    const sparkDirty = needsSparkUpdate;
    if (sparkDirty) {
      sparkRenderer.update({
        scene,
        viewToWorld: camera.matrixWorld,
      });
      needsSparkUpdate = false;
    }
    const splatAnimating = splatAnimationTick(delta);
    const previewAnimating = previewModel?.needsRerender() ?? false;
    if (previewAnimating && previewModel) {
      // Smoothly fade-out preview-model wave animation when splat reveal
      // starts. See `previewFreezeStartTime` declaration above for rationale.
      if (previewFreezeStartTime === null && splatAnimationTime.value > 0) {
        previewFreezeStartTime = currentTime;
      }
      let factor = 1;
      if (previewFreezeStartTime !== null) {
        const t = Math.min(
          (currentTime - previewFreezeStartTime) / PREVIEW_WAVE_FREEZE_DURATION,
          1
        );
        // smoothstep(0, 1, t) → 0..1; factor goes 1→0 smoothly.
        factor = 1 - t * t * (3 - 2 * t);
        // Apply fade to wave-effect uniforms via existing API. Skip after
        // factor settles at 0 to avoid extra GL calls per frame.
        if (factor > 0 || !previewFadeApplied) {
          previewModel.updateWavesConfig({
            wavesColorIntensity: previewWaveInitial.colorIntensity * factor,
            wavesDisplacement: previewWaveInitial.displacement * factor,
          });
          previewFadeApplied = factor === 0;
        }
      }
      previewWaveTime += delta * factor;
      previewModel.update({
        time: previewWaveTime,
        splatRevealTime: (currentDevConfig.keepPreviewModel || currentDevConfig.debugSplatTransform) ? undefined : splatAnimationTime.value,
      });
    }
    rearViewModule.update();
    const environmentAnimating = environmentManager.update(delta);
    renderFullFrame(tellerAnimating);

    const frameDirty = isFrameDirty({
      sparkDirty,
      cameraMovedThisFrame,
      splatAnimating,
      previewAnimating,
      wasPrincipalPointAnimating,
      wasSightTransitioning,
      environmentAnimating,
    });
    updateRenderTier(frameDirty, performance.now());
    if (renderTier !== 'active' && tellerAnimating) {
      if (enterAssistantOnlyMode()) {
        renderTier = 'active';
        idleSinceMs = null;
      } else {
        // If screenshot capture is unavailable (tainted canvas, etc.),
        // keep a slow polling cadence instead of fully pausing.
        renderTier = 'minimal';
      }
    }
    stats?.end();
    scheduleNextFrame();
  }

  function getPoiMarkerDiagnostics(): PoiMarkerDiagnostics[] {
    return poiManager.getDiagnostics();
  }

  window.addEventListener('resize', onResize);
  let containerResizeObserver: ResizeObserver | null = new ResizeObserver(onResize);
  containerResizeObserver.observe(mobilePreviewScreen ?? container);
  document.addEventListener('visibilitychange', onVisibilityChange);
  container.addEventListener('pointerdown', onUserInteraction);
  container.addEventListener('wheel', onUserInteraction, { passive: true });
  container.addEventListener('touchstart', onUserInteraction, { passive: true });
  window.addEventListener('keydown', onKeyInteraction);
  renderSchedulerReady = true;
  requestRender();

  // Tier debug overlay — shown when ?tier is in the URL
  let tierOverlay: HTMLElement | null = null;
  if (urlParams.has('tier')) {
    tierOverlay = document.createElement('div');
    Object.assign(tierOverlay.style, {
      position: 'fixed',
      bottom: '12px',
      left: '12px',
      zIndex: '99999',
      padding: '4px 10px',
      fontSize: '12px',
      fontFamily: 'monospace',
      color: '#fff',
      background: 'rgba(0,0,0,0.6)',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    });

    const benchmarkSuffix = qualityProfile.benchmarkMs != null
      ? ` · ${qualityProfile.benchmarkMs.toFixed(1)}ms`
      : '';
    const cvSuffix = qualityProfile.benchmarkCv != null
      ? ` · cv=${qualityProfile.benchmarkCv.toFixed(2)}`
      : '';
    const label = document.createElement('span');
    label.textContent = `${qualityProfile.deviceClass}${benchmarkSuffix}${cvSuffix}`;
    tierOverlay.appendChild(label);

    const tiers: DeviceTier[] = ['low', 'middle', 'high'];
    for (const tier of tiers) {
      const btn = document.createElement('button');
      btn.textContent = tier;
      const isActive = tier === qualityProfile.tier;
      Object.assign(btn.style, {
        padding: '2px 6px',
        border: `1px solid rgba(255,255,255,${isActive ? '0.6' : '0.3'})`,
        borderRadius: '3px',
        cursor: isActive ? 'default' : 'pointer',
        fontSize: '11px',
        fontFamily: 'monospace',
        color: '#fff',
        background: isActive ? 'rgba(255,255,255,0.25)' : 'transparent',
      });
      if (!isActive) {
        btn.addEventListener('click', () => {
          const nextUrl = new URL(window.location.href);
          nextUrl.searchParams.set('force-tier', tier);
          if (!nextUrl.searchParams.has('tier')) {
            nextUrl.searchParams.set('tier', '');
          }
          window.location.href = nextUrl.toString();
        });
      }
      tierOverlay.appendChild(btn);
    }

    document.body.appendChild(tierOverlay);
  }

  // Stats.js FPS monitor — shown when ?tier is in the URL
  let stats: Stats | null = null;
  if (urlParams.has('tier')) {
    stats = new Stats();
    stats.showPanel(0);
    stats.dom.style.position = 'fixed';
    stats.dom.style.bottom = '40px';
    stats.dom.style.left = '12px';
    stats.dom.style.top = '';
    document.body.appendChild(stats.dom);
  }

  const pause = (): void => {
    if (isDisposed || isPaused) return;
    logEvent('splat pause', { sceneId: config.id });
    isPaused = true;
    renderSchedulerReady = false;
    cancelScheduledFrames();
    exitAssistantOnlyMode();
    controls.enabled = false;
    poiManager.setVisible(false);
    container.style.visibility = 'hidden';
    minimizeDrawingBuffer();
  };

  const resume = (): void => {
    if (isDisposed || !isPaused) return;
    logEvent('splat resume', { sceneId: config.id });
    isPaused = false;
    restoreDrawingBuffer();
    controls.enabled = true;
    poiManager.setVisible(true);
    renderSchedulerReady = true;
    clock.getDelta();
    renderLoop();
    container.style.visibility = '';
    requestRender();
  };

  /**
   * External-facing camera transition (no cinematic side-effects like
   * `previewInputBlocker`). Used by `OverallSceneOverlay` to focus a POI
   * when the user swipes a slide on mobile. `cameraDirector.transitionToPose`
   * auto-cancels any in-flight transition and re-starts from the current
   * camera pose — so rapid swipes interrupt cleanly.
   *
   * Disabled vertical-constraint / height-clamp: overall is a free-orbit
   * scene without Y-clamps, and the caller-provided pose is authoritative.
   * Easing: `ease-in-out` — smooth acceleration/deceleration at both ends
   * for a cinematic feel. Earlier `ease-out` felt abrupt on swipe-start.
   */
  const transitionToPose = (pose: OverviewCameraPose, durationSec: number = 1.5): void => {
    if (isDisposed || !cameraDirector) return;
    cameraDirector.transitionToPose(
      {
        position: new THREE.Vector3(pose.position.x, pose.position.y, pose.position.z),
        target: new THREE.Vector3(pose.target.x, pose.target.y, pose.target.z),
      },
      durationSec,
      undefined, // onComplete
      undefined, // currentTime (auto)
      false,     // applyVerticalConstraint
      'ease-in-out',
      false,     // applyHeightClamp
    );
  };

  /**
   * External-facing wrapper around `runNavigationCinematic`. Used by
   * `OverallSceneOverlay` so the "Explore {scene}" card click feels the
   * same as the 3D nav-POI marker click (cinematic fly-in → scene swap
   * at 70%). NOOP after dispose.
   */
  const navigateToScene = (targetSceneId: string): void => {
    if (isDisposed) return;
    runNavigationCinematic(targetSceneId);
  };

  const dispose = (): void => {
    if (isDisposed) {
      return;
    }
    const disposeStart = performance.now();
    logEvent('splat dispose start', { sceneId: config.id });
    // DIAGNOSTIC: snapshot RIGHT before teardown — max resource count for
    // this scene (all GPU+DOM resources present). Compare with 'post-dispose'
    // to measure what actually gets freed vs what leaks.
    logThreeJsDiagnostics('[splat] pre-dispose', {
      renderer,
      scene,
      sceneId: config.id,
      // Spark/splat private-field probes are accessed reflectively inside
      // the debug module. Pass as-is — the signature accepts `unknown`.
      sparkRenderer,
      splatMesh,
    });
    isDisposed = true;
    stopConfigWatch();
    stopPreviewModelConfigWatch();
    stopBackgroundConfigWatch();
    stopTellerAssistantConfigWatch();
    stopViewModeWatch();
    stopFocusedSightWatch();
    stopOrbitAnimationWatch();
    stopOrbitSightWatch();
    stopGizmoWatch();
    stopOrbitTargetOverrideWatch();
    stopSplatTransformWatch();
    stopSplatVisibleWatch();
    cancelScheduledFrames();
    exitAssistantOnlyMode();
    renderSchedulerReady = false;
    window.removeEventListener('resize', onResize);
    containerResizeObserver?.disconnect();
    document.removeEventListener('visibilitychange', onVisibilityChange);
    container.removeEventListener('pointerdown', onUserInteraction);
    container.removeEventListener('wheel', onUserInteraction);
    container.removeEventListener('touchstart', onUserInteraction);
    window.removeEventListener('keydown', onKeyInteraction);
    removeFlyoverSkipListeners();
    measureDisposeStep('controls.dispose', renderer, () => {
      controls.dispose?.();
    });
    measureDisposeStep('tellerAssistant.dispose', renderer, () => {
      tellerAssistant?.dispose();
    });
    // Order matters (Audit #A4): rearViewModule.dispose() MUST run BEFORE
    // sparkRenderer.cleanup. RearViewSparkModule registers its viewpoint in
    // `spark.autoViewpoints` (autoUpdate: true). If sparkRenderer.cleanup
    // runs first, it iterates autoViewpoints and disposes our viewpoint
    // BEFORE rearViewModule.dispose gets a chance — leading to a redundant
    // (idempotent but wasteful) second `vp.dispose()` call from inside
    // rearViewModule.dispose. Disposing rearViewModule first ensures its
    // viewpoint is properly released into the spark accumulator pool, which
    // sparkRenderer.cleanup then processes during its accumulator sweep.
    measureDisposeStep('rearViewModule.dispose', renderer, () => {
      rearViewModule.dispose();
    });

    // --- Spark/splat GPU cleanup ---
    // Без этих вызовов на iOS остаются висеть десятки/сотни MB GPU памяти:
    //   - SplatMesh.packedSplats.source (DataArrayTexture с splat-атласом)
    //   - compiled dyno shader (objectModifier)
    //   - SparkRenderer internals (SparkViewpoint'ы, shader pool)
    //   - PreviewModel (GLB scene, текстуры, материалы)
    // SparkRenderer сам метода dispose() не имеет — чистим его как
    // обычный THREE.Mesh + диспозим все его SparkViewpoint'ы вручную.
    //
    // Этап 2 (iOS OOM): sparks `PackedSplats.dispose()` освобождает GPU target/
    // source, но НЕ обнуляет `packedSplats.packedArray` — Uint32Array с
    // декодированными сплатами (17-28 MB для 1M-1.8M splats per scene).
    // Этот массив остаётся в main-thread JS heap до тех пор пока PackedSplats
    // instance не будет GC'ed, а iOS JSC медленно собирает такие крупные
    // буферы. Явное обнуление `packedArray = null` освобождает его immediate.
    //
    // Также сохраняем ref на splatMesh для post-dispose диагностики
    // (локальная переменная `splatMesh` обнуляется, но сам инстанс могут
    // удерживать `spark.active.mapping` — см. Класс 1.2 в Этапе 1).
    // Etap 9 (heap snapshot proven, KILLER fix for iOS OOM):
    //
    // Heap snapshot of 7 consecutive splat-scene transitions showed every
    // dead `_SplatMesh2`, `_SparkRenderer2`, `DataArrayTexture` retained via
    // Spark's MODULE-LEVEL `programMaterial: Map<DynoProgram,
    // RawShaderMaterial>` (in `spark.module.js`). Each entry's KEY
    // (DynoProgram) closes over a disposed SplatMesh / SparkRenderer through
    // a DynoBlock graph chain; each entry's VALUE (RawShaderMaterial) holds
    // splat data DataArrayTexture (~30 MB CPU/cycle). Spark provides no
    // public API to clear the Map. Result: ~150 MB JS heap leak per scene
    // transition, OOM-kill on iOS after 3-5 cycles.
    //
    // Patched (`patches/@sparkjsdev__spark.patch`) — MINIMAL & SAFE:
    //   - `clearProgramMaterial()` exported — empties the module-level Map
    //     and disposes each cached material's GPU side.
    //   - `PackedSplats.clearGeneratorPrograms()` static — empties the
    //     class-level `generatorProgram` Map (DynoProgram per scene).
    //
    // The patch does ONLY this. Earlier iterations also extended dispose
    // methods on `SplatMesh / PackedSplats / SparkViewpoint / SparkRenderer`
    // and replaced `_PackedSplats.fullScreenQuad.material` — that broke
    // shader compilation of the next scene's OutputPackedSplat dyno graph
    // (`undefined` literal in fragment shader at the rgbMinMaxLnScaleMinMax
    // position). The minimal patch is sufficient: clearing the Maps removes
    // the last retainer chain; JSC then collects the disconnected
    // DynoProgram + closure scope on its own.
    //
    // Spark internals still need manual reflective cleanup (no public dispose
    // on SparkRenderer) — the original code below handles that.
    //
    // Order matters:
    //   1. splatMesh.dispose() + null packedArray (CPU-side, ~17–28 MB).
    //   2. sparkRenderer reflective cleanup — viewpoints, accumulators,
    //      packedSplats source.data, mesh geometry+material, internal refs.
    //   3. PackedSplats.clearGeneratorPrograms() — empty class-level Map.
    //   4. clearProgramMaterial() — empty module-level Map. This is what
    //      actually breaks the dominant heap-snapshot retainer chain.
    let splatMeshForDiagnostics: SplatMesh | null = null;
    measureDisposeStep('splatMesh.dispose', renderer, () => {
      if (splatMesh) {
        scene.remove(splatMesh);
        // Null main-thread packedArray BEFORE dispose() — spark's dispose
        // doesn't touch it. ~17–28 MB Uint32Array per splat scene.
        const pk = splatMesh.packedSplats as unknown as { packedArray?: unknown } | undefined;
        if (pk) pk.packedArray = null;
        splatMesh.dispose();
        splatMeshForDiagnostics = splatMesh;
        splatMesh = null;
      }
    });
    measureDisposeStep('previewModel.dispose', renderer, () => {
      previewModel?.dispose();
    });
    measureDisposeStep('sparkRenderer.cleanup', renderer, () => {
      scene.remove(sparkRenderer);
      // Spark internals (verified against spark.module.js source):
      // - SparkViewpoint HAS dispose() — releases target + back render targets + SplatGeometry
      // - SplatAccumulator does NOT have dispose() — but has:
      //   * `.splats: PackedSplats` HAS dispose() (releases target + source)
      //   * `.mapping: Array<{node, generator, ...}>` holds STRONG ref to the
      //     disposed SplatMesh (node), blocking GC of its packedArray.
      // - SparkRenderer.splatTexture may be `{ texture: Data3DTexture }` — needs explicit dispose
      // - SparkRenderer.pendingUpdate holds `scene` + a pending `setTimeout(1)`
      //   id; if active at dispose time the timer fires ~1ms later with stale scene.
      interface PackedSplatsInstance {
        dispose: () => void;
        packedArray?: unknown;
        // PackedSplats.source: THREE.DataArrayTexture (uploaded splat-data atlas).
        // texture.dispose() releases GPU side, but `source.data` (Uint8Array,
        // 50–100 MB for 1.5–3M-splat scenes) stays pinned until JS GC.
        source?: { data?: unknown } | null;
      }
      interface SplatAccumulatorLike {
        splats?: PackedSplatsInstance;
        mapping?: Array<unknown>;
      }
      interface SparkPrivateInternals {
        defaultView?: { dispose: () => void } | null;
        autoViewpoints?: Array<{ dispose: () => void }>;
        viewpoint?: { dispose: () => void } | null;
        envViewpoint?: { dispose: () => void } | null;
        active?: SplatAccumulatorLike | null;
        freeAccumulators?: SplatAccumulatorLike[];
        splatTexture?: { texture?: { dispose: () => void } } | null;
        pendingUpdate?: { scene?: unknown; timeoutId?: number };
      }
      const spark = sparkRenderer as unknown as SparkPrivateInternals;

      // Cancel pending setTimeout — its callback would fire ~1ms later with
      // a now-disposed scene reference.
      if (spark.pendingUpdate) {
        if (typeof spark.pendingUpdate.timeoutId === 'number' && spark.pendingUpdate.timeoutId !== -1) {
          try { clearTimeout(spark.pendingUpdate.timeoutId); } catch { /* noop */ }
          spark.pendingUpdate.timeoutId = -1;
        }
        spark.pendingUpdate.scene = null;
      }

      // Dispose all SparkViewpoint instances (dedup via Set — viewpoint often
      // === defaultView, and autoViewpoints may overlap).
      const viewpoints = new Set<{ dispose: () => void }>();
      if (spark.defaultView) viewpoints.add(spark.defaultView);
      if (Array.isArray(spark.autoViewpoints)) {
        for (const v of spark.autoViewpoints) viewpoints.add(v);
      }
      if (spark.viewpoint) viewpoints.add(spark.viewpoint);
      if (spark.envViewpoint) viewpoints.add(spark.envViewpoint);
      for (const vp of viewpoints) {
        try { vp.dispose(); } catch { /* noop */ }
      }

      // Dispose accumulators — their PackedSplats hold heavy GPU + CPU
      // buffers. CRITICAL: null `.source.data` BEFORE dispose to release the
      // CPU-side Uint8Array (DataArrayTexture.source.data) immediately —
      // 50–100 MB per scene. Three.js dispose only releases GPU side.
      // Also clear `.mapping` arrays — they hold STRONG refs to disposed
      // SplatMesh nodes.
      const nullPackedSplatsCpuRefs = (splats: PackedSplatsInstance | undefined): void => {
        if (!splats) return;
        if (splats.source) {
          (splats.source as { data?: unknown }).data = null;
        }
        splats.packedArray = null;
      };
      if (spark.active) {
        const activeSplats = spark.active.splats;
        nullPackedSplatsCpuRefs(activeSplats);
        try { activeSplats?.dispose(); } catch { /* noop */ }
        if (Array.isArray(spark.active.mapping)) spark.active.mapping.length = 0;
      }
      if (Array.isArray(spark.freeAccumulators)) {
        for (const acc of spark.freeAccumulators) {
          nullPackedSplatsCpuRefs(acc?.splats);
          try { acc?.splats?.dispose(); } catch { /* noop */ }
          if (Array.isArray(acc?.mapping)) acc.mapping.length = 0;
        }
        spark.freeAccumulators.length = 0;
      }

      // splatTexture is a SparkRenderer config field that may hold a
      // Data3DTexture for LOD blending. Dispose if present.
      try { spark.splatTexture?.texture?.dispose(); } catch { /* noop */ }

      const sparkMesh = sparkRenderer as unknown as THREE.Mesh;
      sparkMesh.geometry?.dispose();
      const sparkMat = sparkMesh.material;
      if (Array.isArray(sparkMat)) {
        sparkMat.forEach((m) => m.dispose?.());
      } else {
        (sparkMat as THREE.Material | undefined)?.dispose?.();
      }

      // Sever internal back-references on sparkRenderer so its graph is
      // maximally empty when the IIFE closure releases it. Especially
      // important on iOS JSC where large connected graphs are collected
      // lazily.
      spark.active = null;
      spark.defaultView = null;
      spark.viewpoint = null;
      spark.envViewpoint = null;
      if (Array.isArray(spark.autoViewpoints)) spark.autoViewpoints.length = 0;
      spark.splatTexture = null;
      // Break ↔shared-renderer ref. SparkRenderer constructor stores
      // `renderer` (our shared singleton) — if we don't null this, the WHOLE
      // shared THREE.WebGLRenderer is reachable from every disposed
      // sparkRenderer.
      const sparkExt = sparkRenderer as unknown as {
        renderer?: unknown;
        clock?: unknown;
      };
      sparkExt.renderer = null;
      sparkExt.clock = null;
    });

    // Etap 9 KILLER step: empty Spark's internal STATIC caches that retain
    // every DynoProgram + RawShaderMaterial across scene transitions.
    // Without these two calls the heap leaks ~150 MB per cycle. With them,
    // JSC collects the disconnected DynoProgram + closure scope chain
    // (which transitively pins SplatMesh + SparkRenderer + DataArrayTexture).
    // Order: clearGeneratorPrograms first (class-level Map keyed by
    // generator), then clearProgramMaterial (module-level Map keyed by
    // DynoProgram).
    measureDisposeStep('PackedSplats.clearGeneratorPrograms', renderer, () => {
      try { PackedSplats.clearGeneratorPrograms?.(); } catch { /* noop */ }
    });
    measureDisposeStep('clearProgramMaterial', renderer, () => {
      try { clearProgramMaterial?.(); } catch { /* noop */ }
    });

    // Dispose subsystems that own their own GPU resources BEFORE renderer
    // teardown — so textures/geometries land on a still-live WebGL context
    // and get properly freed in renderer.dispose() + forceContextLoss().
    measureDisposeStep('poiManager.dispose', renderer, () => {
      poiManager.dispose();
    });
    measureDisposeStep('devVisuals.dispose', renderer, () => {
      devVisuals.disposeGizmo();
      devVisuals.disposeOrbitCenter();
    });
    measureDisposeStep('sceneBboxDebug.dispose', renderer, () => {
      sceneBboxDebug?.dispose();
      sceneBboxDebug = null;
      bboxDebugRefs.previewModel = null;
      bboxDebugRefs.splatMesh = null;
    });
    // rearViewModule.dispose() MOVED to before sparkRenderer.cleanup
    // (see Audit #A4 comment near the early dispose block above).

    // Release HDR skybox textures (up to 128 MB on iOS with preloaded +
    // loaded EXR). Without this the EnvironmentManager holds refs across
    // scene teardowns.
    measureDisposeStep('environmentManager.dispose', renderer, () => {
      environmentManager.dispose();
    });

    // Safety net for any helper meshes not tracked individually (collider
    // visualizers, dev helpers, overlay geometries). Mirrors the pattern in
    // createGlbScene.ts dispose.
    measureDisposeStep('disposeSceneGraph', renderer, () => {
      disposeSceneGraph(scene);
    });

    // Break scene graph reference chains so Three.js objects can be GC'd
    // independently instead of being held as a big interconnected graph by
    // the dispose closure. iOS JSC is slow to GC large object trees; breaking
    // the chain lets smaller objects get collected in separate GC cycles.
    scene.clear();

    // Etap 7 (heap snapshot proven leak — KILLER fix for cross-scene retention):
    //
    // Heap snapshot retainer trace showed that the active scene's
    // PerspectiveCamera was held by 18+ retainers including all scene
    // subsystems (SightManager, CameraDirector, CameraFlyover,
    // PrincipalPointController, OrbitControls/FirstPersonControls inside
    // SceneControls, RearViewSparkModule, etc). After dispose, these
    // subsystem instances themselves still live (the createScene IIFE
    // closure scope is `system / Context` in the snapshot — pinned by
    // whoever holds `splatApi` or any other createScene closure).
    //
    // Each subsystem stores its `camera/scene/sparkRenderer/renderer/controls`
    // refs as `private readonly` — TS-only, in JS we can null them out.
    // Without this, we observed counters growing per scene transition:
    // SightManager / CameraDirector / PoiMarkerManager / EnvironmentManager
    // / PreviewModel / SceneControls — all going 3 → 4 → 5 across snapshots.
    //
    // Done IN ADDITION to the per-subsystem dispose() calls because most
    // subsystems' dispose() does only GPU cleanup (renderTargets, textures)
    // and leaves these refs intact.
    measureDisposeStep('subsystem-scrub', renderer, () => {
      type AnyRecord = Record<string, unknown>;
      const HEAVY_FIELDS = [
        'scene', 'camera', 'sparkRenderer', 'renderer', 'controls',
        'rearCamera', 'cameraDirector', 'sightManager', 'collisionSystem',
        'splatMesh', 'previewModel', 'environmentManager', 'tellerAssistant',
        'splatRevealOrigin', 'gestureTarget', 'container', 'object', 'target',
      ] as const;
      const subsystemsToScrub: Array<unknown> = [
        controls, sightManager, cameraDirector, cameraFlyover,
        principalPointController, rearViewModule, devVisuals, previewModel,
        environmentManager, poiManager, tellerAssistant, collisionSystem,
        previewTour,
      ];
      let scrubbed = 0;
      for (const sub of subsystemsToScrub) {
        if (sub == null) continue;
        const s = sub as AnyRecord;
        for (const field of HEAVY_FIELDS) {
          if (field in s && s[field] != null) {
            try { s[field] = null; scrubbed++; } catch { /* readonly via Proxy etc */ }
          }
        }
      }
      logEvent('Etap7 subsystem-scrub', { scrubbed });
    });

    // Shared renderer: detach canvas + clear render-list state but do NOT
    // dispose the renderer itself (see `mainRenderer.ts`). Next scene acquires.
    releaseMainRenderer(mainRendererClaimantId);

    if (container.contains(staticFrameOverlay)) {
      container.removeChild(staticFrameOverlay);
    }
    tierOverlay?.remove();
    stats?.dom.remove();
    decrementScene('splat');
    logEvent('splat dispose done', {
      sceneId: config.id,
      durationMs: +(performance.now() - disposeStart).toFixed(1),
    });
    // DIAGNOSTIC: post-dispose snapshot. Compare to 'pre-dispose' — whatever
    // didn't drop to 0/near-0 is a leak. Pass renderer/scene to see if their
    // internal state was actually cleared.
    // Pass sparkRenderer even after cleanup: we WANT to see its private-field
    // state (mapping/accumulators/pendingUpdate) post-dispose. With Этап 2
    // фиксами, `sparkActiveMappingLength` / `sparkActivePackedArrayBytes` /
    // `generatorProgramMapSize` должны упасть до null/0.
    // `splatMeshForDiagnostics` — сохранённый ref на уже disposed SplatMesh.
    // Проверяем, реально ли `packedSplats.packedArray === null` после явного
    // обнуления (Шаг 2.3 → splatMeshPackedArrayBytes должен быть 0, не 17-28MB).
    logThreeJsDiagnostics('[splat] post-dispose', {
      renderer,
      scene,
      sceneId: config.id,
      sparkRenderer,
      splatMesh: splatMeshForDiagnostics,
    });

    // Etap 3 (JS heap retention probes — invisible on iOS without these):
    // Schedule WeakRef checks at +1.5s / +4s / +9s for the heaviest objects.
    // If `stillAlive: true` after 9s, the object is being held by some closure
    // / event listener / store / module-level variable beyond what we're
    // disposing. The probe payload `type` field (constructor name) helps
    // narrow it down ("SplatMesh" / "SparkRenderer" / "Scene" / etc.) and
    // the FINAL probe also dumps non-null property summary via reflection.
    // The probe itself uses ONLY a WeakRef + numeric timer — does not pin
    // the target.
    trackPostDispose('splatMesh', splatMeshForDiagnostics, { sceneId: config.id });
    trackPostDispose('sparkRenderer', sparkRenderer, { sceneId: config.id });
    trackPostDispose('scene', scene, { sceneId: config.id });
    trackPostDispose('camera', camera, { sceneId: config.id });
    if (tellerAssistant) {
      trackPostDispose('tellerAssistant', tellerAssistant, { sceneId: config.id });
    }

    // Etap 4 (closure-scope disconnect): replace api method with no-ops.
    //
    // Why this matters. `dispose`, `pause`, `resume` are closures over the
    // entire `createScene` IIFE scope — scene, camera, splatMesh,
    // sparkRenderer, controls, sightManager, tellerAssistant, etc. ALL of
    // these are declared as `const` and CANNOT be reassigned, so even after
    // we null what we can (splatMesh, splatMeshForDiagnostics, etc), the
    // const refs stay alive as long as ANY closure that captures them is
    // alive. As long as `api` is held externally (React state, Effector
    // store, useEffect closure), the IIFE scope is pinned.
    //
    // Solution: overwrite api methods with no-ops. The OLD closures
    // (dispose/pause/resume) lose their last reference (which was via api),
    // and their captured scope can be GC'd. The api object itself remains
    // (the holder won't see it disappear), but it no longer carries the
    // huge closure chain.
    //
    // Safe because: (1) we're inside dispose, so no further dispose calls
    // happen; (2) pause/resume must be no-ops once disposed anyway (their
    // current implementations early-return on `isDisposed`); (3) the api
    // ref is captured below `return api`, so we can mutate it from here.
    api.dispose = NOOP;
    api.pause = NOOP;
    api.resume = NOOP;
    api.transitionToPose = NOOP;
    api.navigateToScene = NOOP;
    api.getCurrentCameraPose = EMPTY_CAMERA_POSE_GETTER;
    // Neutralise the nav-POI tuner methods. React (NavPoiTuner) and useUnit
    // snapshots may hold the ORIGINAL tuner object even after b.navPoiTuner
    // is nulled below — its method closures captured `poiManager` from the
    // IIFE scope, which is set to null in Etap 8 → late onChange/cleanup
    // calls would throw "Cannot read properties of null". Same pattern as
    // `api.dispose = NOOP` above.
    if (navPoiTuner) {
      navPoiTuner.setDebugDotVisible = NOOP;
      navPoiTuner.setNavPoiPosition = NOOP;
      navPoiTuner.getNavPoiPosition = EMPTY_NAV_POI_POSITION_GETTER;
    }

    // Etap 5 (KILLER fix for iOS heap leak): mutate SceneBindings IN PLACE.
    // The bindings object was passed via `sceneReady` event into the
    // `$sceneBindings` Effector store. Even after `.reset()` zeroes the
    // store value, Effector subscribers / React useUnit snapshots / sample
    // source-readers can still hold the ORIGINAL bindings object. Through
    // its fields (camera, scene, sparkRenderer, controls, sightManager,
    // tellerAssistant — see SceneBindings interface) every retainer pins
    // the entire scene infrastructure. Retention probes proved this:
    // splatApi was empty NOOP'ed, yet scene/camera/sparkRenderer/splatMesh
    // were still alive at +9s.
    //
    // We can't kill the bindings object (don't know who holds it), but we
    // CAN null its fields — leaving a tiny object with only `renderer`
    // (the shared singleton, intentionally retained) and a no-op
    // `getPoiMarkerDiagnostics`. The big stuff becomes unreachable through
    // bindings and can be GC'd independently.
    //
    // Code paths that read bindings.* later get null and must be tolerant —
    // existing callers in `appState.ts` / `assistantHud.ts` already use
    // optional chaining (`source.sceneBindings?.…`) so this is safe.
    if (bindingsForCleanup) {
      const b = bindingsForCleanup as unknown as Record<string, unknown>;
      b.camera = null;
      b.controls = null;
      b.sightManager = null;
      // renderer (shared singleton) intentionally KEPT — it must stay
      // reachable for the next scene's acquireMainRenderer.
      b.sparkRenderer = null;
      b.scene = null;
      b.tellerAssistant = null;
      b.getPoiMarkerDiagnostics = EMPTY_POI_DIAGNOSTICS_GETTER;
      b.getCurrentCameraPose = EMPTY_CAMERA_POSE_GETTER;
      b.forceFinishPreloader = NOOP;
      b.navPoiTuner = null;
      bindingsForCleanup = null;
    }

    // Drop diagnostic ref immediately so the WeakRef can be GC'd as soon as
    // possible (otherwise the local var keeps it pinned for the rest of this
    // function's stack frame).
    splatMeshForDiagnostics = null;

    // Etap 8 (KILLER fix — null IIFE Context slots themselves):
    //
    // Heap snapshot proved that even after dispose:
    //   - `onResize` closure (passed to `new ResizeObserver(...)`) is still
    //     held by `V8ResizeObserverCallback` even though we called
    //     `containerResizeObserver.disconnect()`. WebKit retains it.
    //   - Multiple addEventListener handlers (onKeyInteraction etc) similarly
    //     persist in browser-internal listener lists despite `removeEventListener`.
    //
    // Each retained closure has `[[Scopes]] → createScene IIFE Context (488 bytes,
    // 121 slots)` which holds STRONG refs to scene / camera / sparkRenderer /
    // splatMesh / controls / sightManager / cameraDirector / etc. Earlier
    // mitigations (Etap 5/6/7) nulled subsystem fields, NOT the IIFE slots
    // themselves — the slots kept holding the heavy objects.
    //
    // Solution: declare those locals as `let` (done above) and reassign null
    // here. This empties the IIFE Context slots. Even if the leaked closure
    // persists in WebKit's listener queues, walking through its scope chain
    // finds nothing reachable to keep alive. JSC then collects scene / camera /
    // sparkRenderer / splatMesh independently.
    //
    // `as unknown as T` casts: TypeScript types remain non-nullable so use
    // sites don't need `?.` / `!`. JS at runtime truly assigns null.
    scene = null as unknown as THREE.Scene;
    camera = null as unknown as THREE.PerspectiveCamera;
    sparkRenderer = null as unknown as SparkRenderer;
    devVisuals = null as unknown as DevVisuals;
    collisionSystem = null as unknown as AnalyticalCollisionSystem;
    environmentManager = null as unknown as EnvironmentManager;
    poiManager = null as unknown as PoiMarkerManager;
    rearViewModule = null as unknown as RearViewSparkModule;
    principalPointController = null as unknown as PrincipalPointController;
    controls = null as unknown as SceneControls;
    cameraDirector = null as unknown as CameraDirector;
    previewTour = null as unknown as PreviewTour;
    cameraFlyover = null;  // already nullable (let with `?: ... | null`)
    sightManager = null as unknown as SightManager;
    previewModel = null as unknown as PreviewModel;
    tellerAssistant = null;  // already nullable
    clock = null as unknown as THREE.Clock;
    containerResizeObserver = null;  // already nullable
    bindingsForCleanup = null;  // already nullable (Etap 5)
    // Closure captures camera/controls/scene through its scope; null it so
    // any external retainer doesn't pin the IIFE Context chain.
    forceFinishPreloader = NOOP;
    runNavigationCinematic = NOOP;
    // Dev tuner: drop the global so it doesn't hold poiManager / navigationSights
    // closure refs after dispose. Window-typing is loose because the property
    // is DEV-only and not part of the app's public API.
    delete (window as { __tuneNavPoi?: unknown }).__tuneNavPoi;
    // Note: `renderer` (shared singleton) intentionally NOT nulled — it must
    // survive for the next scene's acquireMainRenderer.
    // `splatMesh` already nulled in splatMesh.dispose step.
  };

  // Materialize the api object as a stable reference so `dispose` can mutate
  // its method properties at end-of-dispose, severing the closure chain.
  // See module-level NOOP / EMPTY_POI_DIAGNOSTICS_GETTER comments for why
  // those are declared OUTSIDE this function.
  const api: SplatSceneApi = {
    dispose,
    pause,
    resume,
    getCurrentCameraPose,
    transitionToPose,
    navigateToScene,
  };
  return api;
}

function resolveSplatSelection(
  splatParam: string | null,
  splatConfig: SceneConfig['splat'],
  qualityProfile: QualityProfile
): { url: string; variant: SceneConfig['splat']['variants'][number] | null } {
  const variantsByKey = new Map(
    splatConfig.variants.map((variant) => [variant.key, variant])
  );
  const variantsByAlias = new Map<string, SceneConfig['splat']['variants'][number]>();
  splatConfig.variants.forEach((variant) => {
    variant.aliases?.forEach((alias) => {
      variantsByAlias.set(alias, variant);
    });
  });

  if (splatParam) {
    const variant = variantsByKey.get(splatParam) ?? variantsByAlias.get(splatParam);
    return { url: variant?.url ?? splatParam, variant: variant ?? null };
  }

  const qualityDefault = splatConfig.defaultByQuality[qualityProfile.name];
  const defaultKey = typeof qualityDefault === 'string'
    ? qualityDefault
    : qualityDefault[qualityProfile.tier];
  const defaultVariant = variantsByKey.get(defaultKey) ?? splatConfig.variants[0] ?? null;
  if (!defaultVariant) {
    throw new Error('Splat configuration is missing a default variant');
  }
  return { url: defaultVariant.url, variant: defaultVariant };
}

function applySplatTransform(
  mesh: THREE.Object3D,
  defaultTransform: SceneTransform | undefined,
  variant: SceneConfig['splat']['variants'][number] | null
): void {
  resetTransform(mesh);
  const overrideDefault = Boolean(variant?.overrideDefaultTransform);
  if (defaultTransform && !overrideDefault) {
    applyTransform(mesh, defaultTransform);
  }
  if (variant?.transform) {
    if (overrideDefault) {
      resetTransform(mesh);
    }
    applyTransform(mesh, variant.transform);
  }
}

function resetTransform(object: THREE.Object3D): void {
  object.position.set(0, 0, 0);
  object.rotation.set(0, 0, 0);
  object.scale.copy(DEFAULT_OBJECT_SCALE);
}

function applyTransform(object: THREE.Object3D, transform: SceneTransform): void {
  if (transform.position) {
    object.position.set(
      transform.position.x,
      transform.position.y,
      transform.position.z
    );
  }
  if (transform.rotation) {
    object.rotation.set(
      transform.rotation.x,
      transform.rotation.y,
      transform.rotation.z
    );
  }
  if (transform.scale) {
    object.scale.set(transform.scale.x, transform.scale.y, transform.scale.z);
  }
}

function toPreviewTransform(
  transform?: SceneTransform
): { position?: THREE.Vector3; rotation?: THREE.Euler; scale?: THREE.Vector3 } | undefined {
  if (!transform) {
    return undefined;
  }
  return {
    position: transform.position
      ? new THREE.Vector3(
          transform.position.x,
          transform.position.y,
          transform.position.z
        )
      : undefined,
    rotation: transform.rotation
      ? new THREE.Euler(
          transform.rotation.x,
          transform.rotation.y,
          transform.rotation.z
        )
      : undefined,
    scale: transform.scale
      ? new THREE.Vector3(transform.scale.x, transform.scale.y, transform.scale.z)
      : undefined,
  };
}
